import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import DOMPurify from "dompurify";
import { useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip as RechartsTooltip,
	ResponsiveContainer,
	PieChart,
	Pie,
	Cell,
	Legend,
} from "recharts";
import { capitalizeFirstLetter, capitalizeWords } from "../../../utils/letters";
import {
	teacherGetRun,
	teacherGetRunAnnouncements,
	teacherGetRunRecordings,
	teacherGetRunParticipants,
	teacherGetRunGrades,
	teacherCreateRunAnnouncement,
	teacherCreateUnit,
	teacherUpdateUnit,
	teacherDeleteUnit,
	teacherCreateModule,
	teacherUpdateModule,
	teacherDeleteModule,
	teacherCreateContent,
	teacherUpdateContent,
	teacherDeleteContent,
	teacherSendAnnouncement,
	teacherUploadFormativeAikenExam,
} from "../../../api/academic";
import { getFileIconClass } from "../../../utils/fileIconUtils";
import CourseMessagesDrawer from "../../course/CourseMessagesDrawer";
import RecordingPlayerModal from "../../course/RecordingPlayerModal";
import Modal from "../../../backoffice/components/Modal";
import RichTextEditor from "../../../backoffice/components/RichTextEditor";
import { formatWeeklyScheduleForDisplay } from "../../../utils/weeklySchedule";
import { formatRut } from "../../../utils/rutUtils";
import { formatDate } from "../../../utils/date";
import TeacherExamTab from "./TeacherExamTab";

function getErrorMessage(e) {
	return e?.response?.data?.error || e?.message || "Ha ocurrido un error.";
}

function escapeRegExp(value) {
	return value.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

function normalizeRutLike(value) {
	return (value ?? "")
		.toString()
		.toLowerCase()
		.replaceAll(/[.\-\s]/g, "");
}

function getFirstApiMessage(value) {
	if (Array.isArray(value)) return value[0] ?? "";
	if (typeof value === "string") return value;
	return "";
}

function getApiErrorMessage(error, fallback) {
	const response = error?.response;
	const statusCode = response?.status;
	const payload = response?.data;

	if (typeof payload === "string") {
		if (statusCode >= 500) {
			return `Error interno del servidor (${statusCode}). Revisa el log del backend para más detalle.`;
		}
		return payload;
	}

	if (payload && typeof payload === "object") {
		const mainMessage =
			payload.error ||
			payload.detail ||
			getFirstApiMessage(payload.non_field_errors);
		const detailMessage = getFirstApiMessage(payload.error_detail);
		const errorCode = payload.error_code;

		if (mainMessage && detailMessage && detailMessage !== mainMessage) {
			return `${mainMessage} (${detailMessage})`;
		}
		if (mainMessage && errorCode) {
			return `${mainMessage} [${errorCode}]`;
		}
		if (mainMessage) {
			return mainMessage;
		}
	}

	if (statusCode) {
		return `Solicitud fallida con estado ${statusCode}.`;
	}

	return error?.message || fallback;
}

function getPlainTextFromHtml(html) {
	if (!html) return "";
	const doc = new DOMParser().parseFromString(html, 'text/html');
	return doc.body.textContent || "";
}

function highlightSearchMatch(value, query) {
	const text = (value ?? "").toString();
	const needle = query.trim();
	if (!needle) return text;

	const matcher = new RegExp(`(${escapeRegExp(needle)})`, "ig");
	const parts = text.split(matcher);
	let cursor = 0;

	return parts.map((part) => {
		const key = `${cursor}-${part}`;
		cursor += part.length;
		if (part.toLowerCase() === needle.toLowerCase()) {
			return (
				<span key={key} className="search-highlight">
					{part}
				</span>
			);
		}
		return <span key={key}>{part}</span>;
	});
}

function highlightRutSearchMatch(value, query) {
	const text = (value ?? "").toString();
	const needle = query.trim();
	if (!needle) return text;

	const normalizedNeedle = normalizeRutLike(needle);
	if (!normalizedNeedle) return text;

	const normalizedText = [];
	const originalIndexes = [];
	for (let idx = 0; idx < text.length; idx += 1) {
		const char = text[idx];
		if (/[.\-\s]/.test(char)) continue;
		normalizedText.push(char.toLowerCase());
		originalIndexes.push(idx);
	}

	const compact = normalizedText.join("");
	const start = compact.indexOf(normalizedNeedle);
	if (start === -1) return highlightSearchMatch(text, needle);

	const originalStart = originalIndexes[start];
	const originalEnd =
		originalIndexes[start + normalizedNeedle.length - 1] + 1;

	const before = text.slice(0, originalStart);
	const hit = text.slice(originalStart, originalEnd);
	const after = text.slice(originalEnd);

	return (
		<>
			<span>{before}</span>
			<span className="search-highlight">{hit}</span>
			<span>{after}</span>
		</>
	);
}

function swapItemsInArray(items, fromIndex, toIndex) {
	if (!Array.isArray(items)) return [];
	if (
		fromIndex < 0 ||
		toIndex < 0 ||
		fromIndex >= items.length ||
		toIndex >= items.length
	) {
		return [...items];
	}
	if (fromIndex === toIndex) {
		return [...items];
	}

	const next = [...items];
	[next[fromIndex], next[toIndex]] = [next[toIndex], next[fromIndex]];
	return next;
}

function flattenModuleContents(contentsByCategory) {
	if (!contentsByCategory || typeof contentsByCategory !== "object") {
		return [];
	}

	return Object.values(contentsByCategory)
		.flatMap((items) => (Array.isArray(items) ? items : []))
		.sort((a, b) => {
			const orderA = Number(a?.order ?? 0);
			const orderB = Number(b?.order ?? 0);
			if (orderA !== orderB) return orderA - orderB;
			return Number(a?.id ?? 0) - Number(b?.id ?? 0);
		});
}

const RESOURCE_PICKER_OPTIONS = [
	{
		id: "file",
		label: "Archivo",
		icon: "ri-file-upload-line",
		type: "resource",
		contentType: "file",
		enabled: true,
		categories: ["material", "clases", "evaluaciones"],
	},
	{
		id: "text",
		label: "Area de texto y medios",
		icon: "ri-text",
		type: "resource",
		contentType: "text",
		enabled: true,
		categories: ["material"],
	},
	{
		id: "page",
		label: "Página",
		icon: "ri-file-text-fill",
		type: "resource",
		contentType: "text",
		enabled: true,
		categories: ["material"],
	},
	{
		id: "image",
		label: "Imagen",
		icon: "ri-image-line",
		type: "resource",
		contentType: "image",
		enabled: true,
		categories: ["material", "clases"],
	},
	{
		id: "video",
		label: "Video",
		icon: "ri-video-line",
		type: "resource",
		contentType: "video",
		enabled: true,
		categories: ["material", "clases"],
	},
	{
		id: "url",
		label: "URI",
		icon: "ri-links-line",
		type: "resource",
		contentType: "text",
		enabled: true,
		categories: ["material", "clases"],
	},
];

function useRun(runId) {
	const [run, setRun] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const load = useCallback(async () => {
		if (!runId) return;
		setLoading(true);
		setError(null);
		try {
			const data = await teacherGetRun(runId);
			setRun(data);
		} catch (e) {
			setError(e?.response?.data?.error || "No se pudo cargar el curso.");
		} finally {
			setLoading(false);
		}
	}, [runId]);

	useEffect(() => {
		load();
	}, [load]);

	return { run, loading, error, reload: load };
}

export default function TeacherCourseDetail() {
	const { runId } = useParams();
	const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
	const { run, loading, error, reload } = useRun(runId);
	const [expandedUnits, setExpandedUnits] = useState({});
	const [modal, setModal] = useState(null);
	const [saving, setSaving] = useState(false);
	const [actionError, setActionError] = useState(null);
	const [messagesDrawerOpen, setMessagesDrawerOpen] = useState(false);
	const [activeTab, setActiveTab] = useState("curso");
	const [participants, setParticipants] = useState([]);
	const [participantsLoading, setParticipantsLoading] = useState(false);
	const [partSearch, setPartSearch] = useState("");
	const [partRowsPerPage, setPartRowsPerPage] = useState(10);
	const [partPage, setPartPage] = useState(1);
	const [partSortField, setPartSortField] = useState("full_name");
	const [partSortDir, setPartSortDir] = useState("asc");

	const [grades, setGrades] = useState([]);
	const [gradesLoading, setGradesLoading] = useState(false);
	const [gradeSearch, setGradeSearch] = useState("");
	const [gradeRowsPerPage, setGradeRowsPerPage] = useState(10);
	const [gradePage, setGradePage] = useState(1);
	const [gradeSortField, setGradeSortField] = useState("rut");
	const [gradeSortDir, setGradeSortDir] = useState("asc");
	const [gradeExporting, setGradeExporting] = useState(false);
	
	const [announcements, setAnnouncements] = useState([]);
	const [announcementsLoading, setAnnouncementsLoading] = useState(false);
	const [announcementSaving, setAnnouncementSaving] = useState(false);
	const [announcementError, setAnnouncementError] = useState(null);
	const [announcementFieldErrors, setAnnouncementFieldErrors] = useState({});
	const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
	const [announcementForm, setAnnouncementForm] = useState({
		subject: "",
		body_html: "",
	});
	const [recordings, setRecordings] = useState([]);
	const [recordingsLoading, setRecordingsLoading] = useState(false);
	const [recordingsError, setRecordingsError] = useState(null);
	const [selectedRecording, setSelectedRecording] = useState(null);
	const [searchRecording, setSearchRecording] = useState("");
	const [recordingsSortDirection, setRecordingsSortDirection] = useState("asc");
	const [recordingsSortField, setRecordingsSortField] = useState("session_date");
	const [recordingsPage, setRecordingsPage] = useState(1);
	const [recordingsRowsPerPage, setRecordingsRowsPerPage] = useState(10);
	const [resourceOrderOverrides, setResourceOrderOverrides] = useState({});
	const [draggedResource, setDraggedResource] = useState(null);
	const [dragOverResource, setDragOverResource] = useState(null);
	const [resourceReordering, setResourceReordering] = useState(false);

	useEffect(() => {
		if (!runId) return;
		setParticipantsLoading(true);
		teacherGetRunParticipants(runId)
			.then((data) => {
				setParticipants(data);
				if (activeTab === "participantes") {
					setPartPage(1);
				}
			})
			.catch(() => setParticipants([]))
			.finally(() => setParticipantsLoading(false));
	}, [activeTab, runId]);

	useEffect(() => {
		if (!runId || activeTab !== "avisos") return;
		let cancelled = false;
		setAnnouncementsLoading(true);
		teacherGetRunAnnouncements(runId)
			.then((data) => {
				if (!cancelled) {
					setAnnouncements(Array.isArray(data) ? data : []);
				}
			})
			.catch(() => {
				if (!cancelled) setAnnouncements([]);
			})
			.finally(() => {
				if (!cancelled) setAnnouncementsLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [activeTab, runId]);

	useEffect(() => {
		if (!runId || activeTab !== "grabaciones") return;
		let cancelled = false;
		setRecordingsLoading(true);
		setRecordingsError(null);
		teacherGetRunRecordings(runId)
			.then((data) => {
				if (!cancelled) {
					setRecordings(Array.isArray(data) ? data : []);
				}
			})
			.catch((error) => {
				if (!cancelled) {
					setRecordings([]);
					setRecordingsError(
						error?.response?.data?.error ||
						"No se pudieron cargar las grabaciones.",
					);
				}
			})
			.finally(() => {
				if (!cancelled) setRecordingsLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [activeTab, runId]);

	useEffect(() => {
		if (activeTab !== "calificaciones" || !runId) return;
		setGradesLoading(true);
		teacherGetRunGrades(runId)
			.then((data) => {
				const gradesList = data.grades || [];
				setGrades(gradesList);
				setGradePage(1);
			})
			.catch(() => {
				setGrades([]);
			})
			.finally(() => setGradesLoading(false));
	}, [activeTab, runId]);

	useEffect(() => {
		setResourceOrderOverrides({});
		setDraggedResource(null);
		setDragOverResource(null);
	}, [run]);

	useEffect(() => {
		if (!draggedResource) return undefined;

		const preventBrowserDropNavigation = (event) => {
			event.preventDefault();
		};

		window.addEventListener("dragover", preventBrowserDropNavigation);
		window.addEventListener("drop", preventBrowserDropNavigation);

		return () => {
			window.removeEventListener(
				"dragover",
				preventBrowserDropNavigation,
			);
			window.removeEventListener("drop", preventBrowserDropNavigation);
		};
	}, [draggedResource]);

	const partFiltered = useMemo(() => {
		const q = partSearch.trim().toLowerCase();
		if (!q) return participants;
		return participants.filter(
			(p) =>
				(p.full_name || "").toLowerCase().includes(q) ||
				(p.email || "").toLowerCase().includes(q) ||
				(p.role || "").toLowerCase().includes(q),
		);
	}, [participants, partSearch]);

	const partSorted = useMemo(() => {
		const data = [...partFiltered];
		data.sort((a, b) => {
			const av = (
				partSortField === "last_login"
					? a.last_login || ""
					: a[partSortField] || ""
			).toLowerCase();
			const bv = (
				partSortField === "last_login"
					? b.last_login || ""
					: b[partSortField] || ""
			).toLowerCase();
			if (av < bv) return partSortDir === "asc" ? -1 : 1;
			if (av > bv) return partSortDir === "asc" ? 1 : -1;
			return 0;
		});
		return data;
	}, [partFiltered, partSortField, partSortDir]);

	const partTotalPages = Math.max(
		1,
		Math.ceil(partSorted.length / partRowsPerPage),
	);
	const partStart = (partPage - 1) * partRowsPerPage;
	const partEnd = Math.min(partStart + partRowsPerPage, partSorted.length);
	const partToShow = partSorted.slice(partStart, partEnd);

	// 1. Filtrado de búsqueda
	const filteredRecordings = useMemo(() => {
		const q = searchRecording.trim().toLowerCase();
		if (!q) return recordings;
		return recordings.filter((r) =>
			(r.title ?? "").toLowerCase().includes(q) ||
			(r.notes ?? "").toLowerCase().includes(q) ||
			(r.session_date ?? "").toLowerCase().includes(q)
		);
	}, [recordings, searchRecording]);

	const sortedRecordings = useMemo(() => {
		const directionFactor = recordingsSortDirection === "asc" ? 1 : -1;
		const sorted = [...filteredRecordings];

		sorted.sort((left, right) => {
			if (recordingsSortField === "session_date") {
				const leftDate = left.session_date ? new Date(left.session_date).getTime() : 0;
				const rightDate = right.session_date ? new Date(right.session_date).getTime() : 0;
				return (leftDate - rightDate) * directionFactor;
			}

			const leftValue = (left[recordingsSortField] ?? "").toString().toLowerCase();
			const rightValue = (right[recordingsSortField] ?? "").toString().toLowerCase();
			return leftValue.localeCompare(rightValue, "es") * directionFactor;
		});
		return sorted;
	}, [filteredRecordings, recordingsSortDirection, recordingsSortField]);

	const totalRecordingRows = sortedRecordings.length;
	const totalRecordingPages = Math.max(1, Math.ceil(totalRecordingRows / recordingsRowsPerPage));
	const safeRecordingsPage = Math.min(recordingsPage, totalRecordingPages);

	useEffect(() => {
		setRecordingsPage(1);
	}, [searchRecording, recordingsRowsPerPage, recordingsSortField, recordingsSortDirection]);

	const paginatedRecordings = sortedRecordings.slice(
		(safeRecordingsPage - 1) * recordingsRowsPerPage,
		safeRecordingsPage * recordingsRowsPerPage
	);

	const gradeFiltered = useMemo(() => {
		const q = gradeSearch.trim().toLowerCase();
		if (!q) return grades;
		const normalizedQ = normalizeRutLike(q);
		return grades.filter((g) => {
			const fullName = (g.full_name || "").toLowerCase();
			const rutRaw = (g.rut || "").toLowerCase();
			const formattedRut = formatRut(g.rut) || g.rut || "";
			const normalizedRut = normalizeRutLike(formattedRut);
			return (
				fullName.includes(q) ||
				rutRaw.includes(q) ||
				(normalizedQ.length > 0 && normalizedRut.includes(normalizedQ))
			);
		});
	}, [grades, gradeSearch]);

	const gradeSorted = useMemo(() => {
		const data = [...gradeFiltered];
		data.sort((a, b) => {
			if (["final_grade", "exam_score"].includes(gradeSortField)) {
				const av = Number.isFinite(Number(a[gradeSortField]))
					? Number(a[gradeSortField])
					: -Infinity;
				const bv = Number.isFinite(Number(b[gradeSortField]))
					? Number(b[gradeSortField])
					: -Infinity;
				if (av < bv) return gradeSortDir === "asc" ? -1 : 1;
				if (av > bv) return gradeSortDir === "asc" ? 1 : -1;
				return 0;
			}
			if (gradeSortField === "exam_finished_at") {
				const av = a.exam_finished_at
					? new Date(a.exam_finished_at).getTime()
					: 0;
				const bv = b.exam_finished_at
					? new Date(b.exam_finished_at).getTime()
					: 0;
				if (av < bv) return gradeSortDir === "asc" ? -1 : 1;
				if (av > bv) return gradeSortDir === "asc" ? 1 : -1;
				return 0;
			}
			const av = (a[gradeSortField] || "").toString().toLowerCase();
			const bv = (b[gradeSortField] || "").toString().toLowerCase();
			if (av < bv) return gradeSortDir === "asc" ? -1 : 1;
			if (av > bv) return gradeSortDir === "asc" ? 1 : -1;
			return 0;
		});
		return data;
	}, [gradeFiltered, gradeSortField, gradeSortDir]);

	const gradeTotalPages = Math.max(
		1,
		Math.ceil(gradeSorted.length / gradeRowsPerPage),
	);
	const gradeStart = (gradePage - 1) * gradeRowsPerPage;
	const gradeEnd = Math.min(
		gradeStart + gradeRowsPerPage,
		gradeSorted.length,
	);
	const gradeToShow = gradeSorted.slice(gradeStart, gradeEnd);

	// El promedio solo considera estudiantes con examen rendido.
	const calculatedGradesAverage = useMemo(() => {
		const gradeValues = grades
			.map((g) => g.final_grade)
			.filter((grade) => Number.isFinite(grade));
		if (gradeValues.length === 0) return null;
		return gradeValues.reduce((sum, g) => sum + g, 0) / gradeValues.length;
	}, [grades]);

	const gradeStats = useMemo(() => {
		const graded = grades
			.map((g) => g.final_grade)
			.filter((grade) => Number.isFinite(grade));

		const totalStudents = grades.length;
		const takenCount = graded.length;
		const absentCount = Math.max(0, totalStudents - takenCount);

		if (takenCount === 0) {
			return {
				max: null,
				min: null,
				avg: null,
				stdDev: null,
				takenCount,
				absentCount,
			};
		}

		const sum = graded.reduce((acc, grade) => acc + grade, 0);
		const avg = sum / takenCount;
		const variance =
			graded.reduce(
				(acc, grade) => acc + (grade - avg) * (grade - avg),
				0,
			) / takenCount;

		return {
			max: Math.max(...graded),
			min: Math.min(...graded),
			avg,
			stdDev: Math.sqrt(variance),
			takenCount,
			absentCount,
		};
	}, [grades]);

	const gradeChartData = useMemo(() => {
		const graded = grades
			.map((g) => g.final_grade)
			.filter((g) => Number.isFinite(g));

		const ranges = [
			{ label: "1.0-1.9", min: 1.0, max: 1.9 },
			{ label: "2.0-2.9", min: 2.0, max: 2.9 },
			{ label: "3.0-3.9", min: 3.0, max: 3.9 },
			{ label: "4.0-4.9", min: 4.0, max: 4.9 },
			{ label: "5.0-5.9", min: 5.0, max: 5.9 },
			{ label: "6.0-7.0", min: 6.0, max: 7.0 },
		];

		const histogram = ranges.map(({ label, min, max }) => ({
			label,
			count: graded.filter((g) => g >= min && g <= max).length,
			aprobado: min >= 4.0,
		}));

		const aprobados = graded.filter((g) => g >= 4.0).length;
		const reprobados = graded.filter((g) => g < 4.0).length;

		const donut = [
			{ name: "Aprobados", value: aprobados },
			{ name: "Reprobados", value: reprobados },
		];

		return { histogram, donut };
	}, [grades]);

	const roundTraditionalTwoDecimals = (value) => {
		return Math.round((value + Number.EPSILON) * 10) / 10;
	};

	const formatGradeStat = (value) => {
		if (!Number.isFinite(value)) return "-";
		return roundTraditionalTwoDecimals(value).toFixed(1);
	};

	const handleExportGradesExcel = () => {
		if (grades.length === 0) return;

		setGradeExporting(true);
		try {
			const exportSource = [...grades].sort((a, b) => {
				const av = (a.rut || "").toString().toLowerCase();
				const bv = (b.rut || "").toString().toLowerCase();
				if (av < bv) return gradeSortDir === "asc" ? -1 : 1;
				if (av > bv) return gradeSortDir === "asc" ? 1 : -1;
				return 0;
			});

			const exportRows = exportSource.map((g) => ({
				RUT: g.rut ? formatRut(g.rut) : "-",
				"Nombre / Apellidos": capitalizeWords(g.full_name || ""),
				"Puntaje examen":
					g.exam_score !== null && g.exam_score !== undefined
						? `${Number(g.exam_score).toFixed(1)}%`
						: "-",
				"Fecha examen": g.exam_finished_at
					? formatDate(g.exam_finished_at)
					: "-",
				"Nota final":
					g.final_grade !== null && g.final_grade !== undefined
						? Number(g.final_grade).toFixed(1)
						: "-",
			}));

			const worksheet = XLSX.utils.json_to_sheet(exportRows, {
				header: [
					"RUT",
					"Nombre / Apellidos",
					"Puntaje examen",
					"Fecha examen",
					"Nota final",
				],
			});
			worksheet["!cols"] = [
				{ wch: 16 },
				{ wch: 40 },
				{ wch: 18 },
				{ wch: 16 },
				{ wch: 12 },
			];

			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, "Calificaciones");

			const today = new Date().toISOString().slice(0, 10);
			const courseSlug = (
				run?.course_name || `curso_${runId || "sin_id"}`
			)
				.toString()
				.replace(/[^\w-]+/g, "_")
				.slice(0, 50);

			XLSX.writeFile(
				workbook,
				`calificaciones_${courseSlug}_${today}.xlsx`,
				{ compression: true },
			);
		} catch (e) {
			setActionError(
				e?.message ||
				"No se pudo exportar el archivo Excel de calificaciones.",
			);
		} finally {
			setGradeExporting(false);
		}
	};

	const tieneContenido = (htmlText) => {
		return htmlText && htmlText.replace(/<[^>]+>/g, "").trim() !== "";
	};

	const traducirModalidad = (mod) => {
		if (mod === "sync") return "Sincrónico";
		if (mod === "async") return "Asincrónico";
		if (mod === "mixed") return "Mixto";
		if (mod === "presencial") return "Presencial";
		return "Por definir";
	};

	useEffect(() => {
		if (!modal) return;
		setActionError(null);
		const onKeyDown = (e) => {
			if (e.key === "Escape") setModal(null);
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [modal]);

	const toggleUnit = (id) => {
		setExpandedUnits((prev) => ({ ...prev, [id]: !prev[id] }));
	};

	const allUnitsExpanded =
		(run?.units || []).length > 0 &&
		(run.units || []).every((unit) => expandedUnits[unit.id] !== false);

	const toggleAllUnits = () => {
		const units = run?.units || [];
		if (!units.length) return;
		setExpandedUnits(
			Object.fromEntries(
				units.map((unit) => [unit.id, !allUnitsExpanded ? true : false]),
			),
		);
	};

	const getResourceOrderKey = (moduleId, category) =>
		`${moduleId}:${category}`;

	const getVisibleResources = (moduleId, category, fallbackItems) => {
		const key = getResourceOrderKey(moduleId, category);
		return resourceOrderOverrides[key] || fallbackItems || [];
	};

	const handleAnnouncementFieldChange = useCallback((field, value) => {
		setAnnouncementForm((prev) => ({ ...prev, [field]: value }));
		setAnnouncementError(null);
		setAnnouncementFieldErrors((prev) => {
			if (!prev[field]) return prev;
			const next = { ...prev };
			delete next[field];
			return next;
		});
	}, []);

	const handleAnnouncementSubmit = useCallback(
		async (event) => {
			event.preventDefault();
			const subject = (announcementForm.subject || "").trim();
			const bodyText = getPlainTextFromHtml(announcementForm.body_html);
			const nextErrors = {};

			if (!subject) {
				nextErrors.subject = "El asunto es obligatorio.";
			}
			if (!bodyText) {
				nextErrors.body_html = "El mensaje es obligatorio.";
			}

			if (Object.keys(nextErrors).length > 0) {
				setAnnouncementFieldErrors(nextErrors);
				return;
			}

			setAnnouncementSaving(true);
			setAnnouncementError(null);
			setAnnouncementFieldErrors({});

			try {
				const created = await teacherCreateRunAnnouncement(runId, {
					subject,
					body_html: announcementForm.body_html,
				});
				setAnnouncements((prev) => [created, ...prev]);
				setAnnouncementForm({ subject: "", body_html: "" });
			} catch (e) {
				const data = e?.response?.data;
				const subjectError = getFirstApiMessage(data?.subject);
				const bodyError = getFirstApiMessage(data?.body_html);
				if (subjectError || bodyError) {
					setAnnouncementFieldErrors({
						...(subjectError ? { subject: subjectError } : {}),
						...(bodyError ? { body_html: bodyError } : {}),
					});
				}
				setAnnouncementError(
					getApiErrorMessage(
						e,
						"No se pudo enviar el aviso a los estudiantes.",
					),
				);
			} finally {
				setAnnouncementSaving(false);
			}
		},
		[announcementForm.body_html, announcementForm.subject, runId],
	);

	const openAnnouncementDetail = useCallback((announcement) => {
		if (!announcement || typeof announcement !== "object") return;
		setSelectedAnnouncement(announcement);
	}, []);

	const closeAnnouncementDetail = useCallback(() => {
		setSelectedAnnouncement(null);
	}, []);

	const handleResourceDragStart = (event, moduleId, category, index) => {
		if (resourceReordering) return;
		event.stopPropagation();
		const payload = { moduleId, category, index };
		setDraggedResource(payload);
		event.dataTransfer.effectAllowed = "move";
		event.dataTransfer.setData("text/plain", JSON.stringify(payload));
	};

	const handleResourceItemDragStart = (
		event,
		moduleId,
		category,
		index,
	) => {
		const dragHandle = event.target.closest(".aula-resource-drag-handle");
		const interactiveTarget = event.target.closest(
			"button, a, select, input, textarea, label, [role='button']",
		);

		// Permite arrastrar desde el item, pero evita mezclar con controles de accion.
		if (interactiveTarget && !dragHandle) {
			event.preventDefault();
			return;
		}

		handleResourceDragStart(event, moduleId, category, index);
	};

	const handleResourceItemMouseDown = (event) => {
		const interactiveTarget = event.target.closest(
			"button, a, select, input, textarea, label, [role='button']",
		);

		// Evita seleccion de texto al mantener clic sobre el item arrastrable.
		if (!interactiveTarget) {
			event.preventDefault();
		}
	};

	const handleResourceDragOver = (event, moduleId, category, index) => {
		event.preventDefault();
		event.stopPropagation();
		let source = draggedResource;
		if (!source) {
			try {
				source = JSON.parse(
					event.dataTransfer.getData("text/plain") || "null",
				);
			} catch {
				source = null;
			}
		}
		const rect = event.currentTarget.getBoundingClientRect();
		const isAfter = event.clientY > rect.top + rect.height / 2;
		setDragOverResource({
			moduleId,
			category,
			index,
			position: isAfter ? "after" : "before",
			sourceModuleId: source?.moduleId ?? null,
			sourceCategory: source?.category ?? null,
			sourceIndex: Number.isInteger(source?.index)
				? source.index
				: null,
		});
	};

	const handleResourceDragEnd = () => {
		setDraggedResource(null);
		setDragOverResource(null);
	};

	const handleResourceListDragOver = (event) => {
		event.preventDefault();
		event.stopPropagation();
	};

	const handleResourceListDrop = (event) => {
		event.preventDefault();
		event.stopPropagation();
	};

	const handleResourceDrop = async (
		event,
		moduleId,
		category,
		targetIndex,
		dropPosition,
		fallbackItems,
	) => {
		event.preventDefault();
		event.stopPropagation();

		let source = draggedResource;
		if (!source) {
			try {
				source = JSON.parse(
					event.dataTransfer.getData("text/plain") || "null",
				);
			} catch {
				source = null;
			}
		}

		if (!source) return;
		if (source.moduleId !== moduleId || source.category !== category) {
			setDraggedResource(null);
			setDragOverResource(null);
			return;
		}

		const current = getVisibleResources(moduleId, category, fallbackItems);
		const reordered = swapItemsInArray(
			current,
			source.index,
			targetIndex,
		);
		if (!reordered.length) {
			setDraggedResource(null);
			setDragOverResource(null);
			return;
		}

		const key = getResourceOrderKey(moduleId, category);
		setResourceOrderOverrides((prev) => ({ ...prev, [key]: reordered }));
		setResourceReordering(true);
		setActionError(null);

		try {
			await Promise.all(
				reordered.map((item, idx) =>
					teacherUpdateContent(item.id, { order: idx + 1 }),
				),
			);
		} catch (e) {
			setActionError(getErrorMessage(e));
			setResourceOrderOverrides((prev) => {
				const next = { ...prev };
				delete next[key];
				return next;
			});
		} finally {
			setResourceReordering(false);
			setDraggedResource(null);
			setDragOverResource(null);
		}
	};

	const clearActionError = () => setActionError(null);

	const selectedAnnouncementSentAt =
		selectedAnnouncement?.email_sent_at || selectedAnnouncement?.created_at;

	const handleAddUnit = async (title) => {
		if (!title?.trim()) return;
		setSaving(true);
		setActionError(null);
		try {
			await teacherCreateUnit(runId, { title: title.trim() });
			reload();
			setModal(null);
		} catch (e) {
			setActionError(getErrorMessage(e));
		} finally {
			setSaving(false);
		}
	};

	const handleEditUnit = async (unitId, title) => {
		if (!title?.trim()) return;
		setSaving(true);
		setActionError(null);
		try {
			await teacherUpdateUnit(unitId, { title: title.trim() });
			reload();
			setModal(null);
		} catch (e) {
			setActionError(getErrorMessage(e));
		} finally {
			setSaving(false);
		}
	};

	const handleDeleteUnit = async (unitId) => {
		setSaving(true);
		setActionError(null);
		try {
			await teacherDeleteUnit(unitId);
			reload();
			setModal(null);
		} catch (e) {
			setActionError(getErrorMessage(e));
		} finally {
			setSaving(false);
		}
	};

	const handleUploadFormativeExam = async (unitId, file) => {
		if (!file) return;
		setSaving(true);
		setActionError(null);
		try {
			await teacherUploadFormativeAikenExam(unitId, file);
			reload();
			setModal(null);
		} catch (e) {
			setActionError(getErrorMessage(e));
		} finally {
			setSaving(false);
		}
	};

	const handleAddWeek = async (unitId, title) => {
		setSaving(true);
		setActionError(null);
		try {
			await teacherCreateModule(unitId, {
				title: title?.trim() || "Nueva semana",
			});
			reload();
			setModal(null);
		} catch (e) {
			setActionError(getErrorMessage(e));
		} finally {
			setSaving(false);
		}
	};

	const handleEditWeek = async (moduleId, title) => {
		if (!title?.trim()) return;
		setSaving(true);
		setActionError(null);
		try {
			await teacherUpdateModule(moduleId, { title: title.trim() });
			reload();
			setModal(null);
		} catch (e) {
			setActionError(getErrorMessage(e));
		} finally {
			setSaving(false);
		}
	};

	const handleDeleteWeek = async (moduleId) => {
		setSaving(true);
		setActionError(null);
		try {
			await teacherDeleteModule(moduleId);
			reload();
			setModal(null);
		} catch (e) {
			setActionError(getErrorMessage(e));
		} finally {
			setSaving(false);
		}
	};

	const handleAddResource = async (moduleId, payload, file) => {
		setSaving(true);
		setActionError(null);
		try {
			await teacherCreateContent(moduleId, payload, file);
			reload();
			setModal(null);
		} catch (e) {
			setActionError(getErrorMessage(e));
		} finally {
			setSaving(false);
		}
	};

	const handleEditResource = async (contentId, payload, file) => {
		setSaving(true);
		setActionError(null);
		try {
			await teacherUpdateContent(contentId, payload, file);
			reload();
			setModal(null);
		} catch (e) {
			setActionError(getErrorMessage(e));
		} finally {
			setSaving(false);
		}
	};

	const handleDeleteResource = async (contentId) => {
		setSaving(true);
		setActionError(null);
		try {
			await teacherDeleteContent(contentId);
			reload();
			setModal(null);
		} catch (e) {
			setActionError(getErrorMessage(e));
		} finally {
			setSaving(false);
		}
	};

	if (loading) {
		return (
			<div className="loader-container">
				<img
					src="/icon/carga-puntos.svg"
					alt="Cargando..."
					style={{ width: "180px" }}
				/>
				<p className="txt-loading">Cargando contenidos...</p>
			</div>
		);
	}
	if (error) {
		return (
			<div className="teacher-page teacher-course-page">
				<div className="alert alert-danger teacher-course-error">
					{error}
				</div>
			</div>
		);
	}
	if (!run) return null;

	const courseName = run.name || run.code || "Curso";
	const courseMainLine = [
		run.period_label || run.period || run.academic_period,
		run.code,
		run.company_name || run.campus || run.location,
	]
		.filter(Boolean)
		.join(" / ");

	return (
		<>
			<div className="teacher-page teacher-course-page p-4 backoffice-tables flex-grow-1 overflow-auto font-n">
				{actionError && (
					<div
						className="alert alert-danger d-flex align-items-center justify-content-between mb-3"
						role="alert"
					>
						<span>{actionError}</span>
						<button
							type="button"
							className="btn-close"
							onClick={clearActionError}
							aria-label="Cerrar"
						/>
					</div>
				)}

				<div className="aula-layout">

					<div className="aula-main">
						<div className="aula-card teacher-course-hero-card">
							<header className="teacher-course-mainhead">
								<div
									className="teacher-course-mainhead-accent"
									aria-hidden="true"
								/>
								<div className="teacher-course-mainhead-body">
									<h1 className="teacher-course-mainhead-title">
										{capitalizeFirstLetter(courseName)}
									</h1>
									{courseMainLine && (
										<p className="pt-3 mb-0">
											{courseMainLine}
										</p>
									)}
									<button
										type="button"
										className="btn btn-link mt-2 mb-2 p-0 text-decoration-none aula-ver-descripcion"
										onClick={() => setIsInfoModalOpen(true)}>
										<span>Contenido del curso</span>
									</button>

									<ul
										className="teacher-course-mainhead-tabs"
										aria-label="Secciones del curso"
									>
										{[
											{ key: "curso", label: "Curso" },
											{
												key: "grabaciones",
												label: "Grabaciones",
											},
											{
												key: "participantes",
												label: "Participantes",
											},
											{
												key: "calificaciones",
												label: "Calificaciones",
											},
											{
												key: "examen",
												label: "Examen Final",
											},
											// Avisos deshabilitado para entrega final.
										].map(({ key, label }) => (
											<li
												key={key}
												className={
													activeTab === key
														? "active"
														: ""
												}
												onClick={() =>
													setActiveTab(key)
												}
												style={{ cursor: "pointer" }}
											>
												{label}
											</li>
										))}
									</ul>
								</div>
							</header>

							{activeTab === "participantes" ? (
								<div className="teacher-participants-panel">
									{participantsLoading ? (
										<p className="txt-loading px-3 py-4">
											Cargando participantes...
										</p>
									) : (
										<div className="card-body p-0">
											<div className="table-toolbar table-toolbar-locked">
												<div className="toolbar-group toolbar-pagination-group">
													<button
														type="button"
														className="toolbar-nav-btn"
														onClick={() =>
															setPartPage(1)
														}
														disabled={
															partPage === 1
														}
													>
														&lt;&lt;
													</button>
													<button
														type="button"
														className="toolbar-nav-btn"
														onClick={() =>
															setPartPage((p) =>
																Math.max(
																	1,
																	p - 1,
																),
															)
														}
														disabled={
															partPage === 1
														}
													>
														&lt;
													</button>
													<select
														className="form-select toolbar-select toolbar-page-select"
														value={partPage}
														onChange={(e) =>
															setPartPage(
																Number(
																	e.target
																		.value,
																),
															)
														}
														disabled={
															partTotalPages <= 1
														}
													>
														{Array.from(
															{
																length: partTotalPages,
															},
															(_, i) => i + 1,
														).map((pg) => (
															<option
																key={pg}
																value={pg}
															>
																{pg}
															</option>
														))}
													</select>
													<button
														type="button"
														className="toolbar-nav-btn"
														onClick={() =>
															setPartPage((p) =>
																Math.min(
																	partTotalPages,
																	p + 1,
																),
															)
														}
														disabled={
															partPage >=
															partTotalPages
														}
													>
														&gt;
													</button>
													<button
														type="button"
														className="toolbar-nav-btn"
														onClick={() =>
															setPartPage(
																partTotalPages,
															)
														}
														disabled={
															partPage >=
															partTotalPages
														}
													>
														&gt;&gt;
													</button>
												</div>

												<div className="search-group toolbar-search-group d-flex align-items-center gap-2">
													<input
														className="form-control"
														value={partSearch} onChange={(e) => {
															setPartSearch(
																e.target.value,
															);
															setPartPage(1);
														}}
														placeholder="Buscar por nombre, correo o rol"
														maxLength={120}
													/>
													<button
														type="button"
														className="toolbar-nav-btn"
														onClick={() => {
															setPartSearch("");
															setPartPage(1);
														}}
														disabled={
															!partSearch.trim()
														}
														title="Limpiar búsqueda"
														aria-label="Limpiar búsqueda"
													>
														<i className="ri-eraser-line" />
													</button>
												</div>

												<div className="toolbar-group toolbar-controls-group">
													<label className="d-flex align-items-center gap-2 mb-0">
														<span>
															Numero de filas:
														</span>
														<select
															className="form-select toolbar-select"
															value={
																partRowsPerPage
															}
															onChange={(e) => {
																setPartRowsPerPage(
																	Number(
																		e.target
																			.value,
																	),
																);
																setPartPage(1);
															}}
														>
															<option value={10}>
																10
															</option>
															<option value={25}>
																25
															</option>
															<option value={50}>
																50
															</option>
															<option value={100}>
																100
															</option>
														</select>
													</label>
													<label className="d-flex align-items-center gap-2 mb-0">
														<span>
															Ordenar por:
														</span>
														<select
															className="form-select toolbar-select"
															value={
																partSortField
															}
															onChange={(e) =>
																setPartSortField(
																	e.target
																		.value,
																)
															}
														>
															<option value="full_name">
																Nombre
															</option>
															<option value="email">
																Correo
															</option>
															<option value="role">
																Rol
															</option>
															<option value="last_login">
																Ultimo acceso
															</option>
														</select>
													</label>
													<button
														type="button"
														className="toolbar-order-btn"
														onClick={() =>
															setPartSortDir(
																(d) =>
																	d === "asc"
																		? "desc"
																		: "asc",
															)
														}
													>
														{partSortDir === "asc"
															? "Asc"
															: "Desc"}
													</button>
												</div>
											</div>

											<div className="table-responsive table-wrapper-scroll">
												<table className="table table-sm table-hover mb-0 align-middle table-organizations-nowrap">
													<thead className="table-light">
														<tr>
															<th>
																<span>
																	Nombre /
																	Apellido(s)
																</span>
															</th>
															<th>
																<span>
																	correo
																</span>
															</th>
															<th>
																<span>
																	Roles
																</span>
															</th>
															<th>
																<span>
																	Ultimo
																	acceso al
																	curso
																</span>
															</th>
														</tr>
													</thead>
													<tbody>
														{partToShow.length ===
															0 ? (
															<tr>
																<td
																	colSpan={4}
																	className="text-muted p-4"
																>
																	<span>
																		{partSearch.trim()
																			? "Sin resultados para la búsqueda."
																			: "No hay participantes registrados."}
																	</span>
																</td>
															</tr>
														) : (
															partToShow.map(
																(p) => (
																	<tr
																		key={
																			p.id
																		}
																	>
																		<td>
																			<span>
																				{capitalizeWords(
																					p.full_name,
																				)}
																			</span>
																		</td>
																		<td>
																			<span>
																				{p.email ||
																					"-"}
																			</span>
																		</td>
																		<td>
																			<span
																				className={`teacher-participants-role ${p.role ===
																					"Estudiante"
																					? "teacher-participants-role--student"
																					: "teacher-participants-role--teacher"
																					}`}
																			>
																				{
																					p.role
																				}
																			</span>
																		</td>
																		<td>
																			<span>
																				{p.last_login
																					? new Date(
																						p.last_login,
																					).toLocaleString(
																						"es-ES",
																					)
																					: "Nunca"}
																			</span>
																		</td>
																	</tr>
																),
															)
														)}
													</tbody>
												</table>
											</div>
											<div className="table-footer-summary">
												<span>
													Mostrando{" "}
													{partSorted.length === 0
														? 0
														: partStart + 1}{" "}
													- {partEnd} de{" "}
													{partSorted.length}{" "}
													registros
												</span>
											</div>
										</div>
									)}
								</div>
							) : activeTab === "grabaciones" ? (
								<section className="card shadow-sm border-0 mt-4">
									<div className="card-body p-0">
										{/* Toolbar de Control */}
										<div className="table-toolbar table-toolbar-locked">
											<div className="toolbar-group toolbar-pagination-group">
												<button
													type="button"
													className="toolbar-nav-btn"
													onClick={() => setRecordingsPage(1)}
													disabled={recordingsPage === 1}
												>
													&lt;&lt;
												</button>
												<button
													type="button"
													className="toolbar-nav-btn"
													onClick={() => setRecordingsPage(p => Math.max(p - 1, 1))}
													disabled={recordingsPage === 1}
												>
													&lt;
												</button>
												<select
													className="form-select toolbar-select toolbar-page-select"
													value={recordingsPage}
													onChange={(e) => setRecordingsPage(Number(e.target.value))}
													disabled={totalRecordingPages <= 1}
												>
													{Array.from({ length: totalRecordingPages }, (_, i) => i + 1).map(p => (
														<option key={p} value={p}>{p}</option>
													))}
												</select>
												<button
													type="button"
													className="toolbar-nav-btn"
													onClick={() => setRecordingsPage(p => Math.min(p + 1, totalRecordingPages))}
													disabled={recordingsPage >= totalRecordingPages}
												>
													&gt;
												</button>
												<button
													type="button"
													className="toolbar-nav-btn"
													onClick={() => setRecordingsPage(totalRecordingPages)}
													disabled={recordingsPage >= totalRecordingPages}
												>
													&gt;&gt;
												</button>
											</div>

											<div className="search-group toolbar-search-group d-flex align-items-center gap-2">
												<input
													className="form-control"
													placeholder="Buscar grabación..."
													value={searchRecording}
													onChange={(e) => {
														setSearchRecording(e.target.value);
														setRecordingsPage(1);
													}}
												/>
												<button
													type="button"
													className="toolbar-nav-btn"
													onClick={() => {
														setSearchRecording("");
														setRecordingsPage(1);
													}}
													disabled={!searchRecording.trim()}
													title="Limpiar búsqueda"
													aria-label="Limpiar búsqueda"
												>
													<i className="ri-eraser-line" />
												</button>
											</div>

											<div className="toolbar-group toolbar-controls-group">
												<label className="d-flex align-items-center gap-2 mb-0">
													<span>Numero de filas:</span>
													<select
														className="form-select toolbar-select"
														value={recordingsRowsPerPage}
														onChange={(e) => {
															setRecordingsRowsPerPage(Number(e.target.value));
															setRecordingsPage(1);
														}}
													>
														<option value={10}>10</option>
														<option value={25}>25</option>
														<option value={50}>50</option>
														<option value={100}>100</option>
													</select>
												</label>
												<label className="d-flex align-items-center gap-2 mb-0">
													<span>Ordenar por:</span>
													<select
														className="form-select toolbar-select"
														value={recordingsSortField}
														onChange={(e) => setRecordingsSortField(e.target.value)}
													>
														<option value="title">Título</option>
														<option value="session_date">Fecha</option>
													</select>
												</label>
												<button
													type="button"
													className="toolbar-order-btn"
													onClick={() => setRecordingsSortDirection((d) => d === "asc" ? "desc" : "asc")}
												>
													{recordingsSortDirection === "asc" ? "Asc" : "Desc"}
												</button>
											</div>
										</div>

										{/* Tabla */}
										<div className="table-responsive table-wrapper-scroll">
											<table className="table table-sm table-hover mb-0 align-middle table-organizations-nowrap">
												<thead className="table-light">
													<tr>
														<th><span>Título</span></th>
														<th><span>Fecha</span></th>
														<th><span>Notas</span></th>
														<th className="text-center"><span>Acción</span></th>
													</tr>
												</thead>
												<tbody>
													{paginatedRecordings.map((r) => (
														<tr key={r.id}>
															<td><span className="fw-bold">{capitalizeFirstLetter(r.title || "Sin título")}</span></td>
															<td><span>{formatDate(r.session_date)}</span></td>
															<td><span className="small text-muted text-break">{capitalizeFirstLetter(r.notes) || "—"}</span></td>
															<td className="text-center">
																<button
																	type="button"
																	className="btn-sm adm-see-btn border-0"
																	title="Ver grabación"
																	aria-label={`Ver grabación ${r.title || ""}`.trim()}
																	onClick={() => setSelectedRecording(r)}
																>
																	<i className="ri-play-mini-fill"></i>
																</button>
															</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									</div>
								</section>
							) : activeTab === "avisos" ? (
								<div>
									<div className="div-contenido-doc">
										<div>
											<h2 className="aula-section-title mb-0">
												Avisos del curso
											</h2>
											<p className="text-muted mb-0 fs-6">
												Cada aviso se envia por correo a los estudiantes activos asociados a esta asignatura.
											</p>
										</div>
									</div>
									<section className="card mb-3 mt-4 card-table">
										<div className="card-body">
											<form
												onSubmit={handleAnnouncementSubmit}
											>
												<div className="teacher-announcement-field">
													<label htmlFor="teacher-announcement-subject">
														<span>Asunto</span>
													</label>
													<input
														id="teacher-announcement-subject"
														type="text"
														className={`form-control ${announcementFieldErrors.subject ? "is-invalid" : ""}`}
														value={announcementForm.subject} onChange={(e) => handleAnnouncementFieldChange("subject", e.target.value)
														}
														maxLength={255}
														placeholder="Ej. Cambio de horario o recordatorio de evaluación"
													/>
													{announcementFieldErrors.subject && (
														<div className="invalid-feedback d-block">
															{announcementFieldErrors.subject}
														</div>
													)}
												</div>

												<div className="teacher-announcement-field mt-3">
													<label><span>Mensaje</span></label>
													<RichTextEditor
														value={announcementForm.body_html}
														onChange={(value) =>
															handleAnnouncementFieldChange("body_html", value)
														}
														placeholder="Redacta el aviso que recibiran los estudiantes..."
														error={Boolean(announcementFieldErrors.body_html)}
													/>
													{announcementFieldErrors.body_html && (
														<div className="invalid-feedback d-block">
															{announcementFieldErrors.body_html}
														</div>
													)}
												</div>

												{announcementError && (
													<div className="alert alert-danger py-2 px-3 mb-0">
														{announcementError}
													</div>
												)}

												<div className="teacher-announcement-actions mt-3">
													<p className="teacher-announcement-hint mb-0">
														Se notificará a todos los estudiantes activos matriculados en el curso.
													</p>
													<button
														type="submit"
														className="adm-save-btn d-inline-flex align-items-center gap-2"
														disabled={announcementSaving}
													>
														{announcementSaving ? (
															<>
																<span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
																<span>Enviando...</span>
															</>
														) : (
															<>
																<i className="ri-mail-send-line d-md-none d-inline" />
																<span className="d-md-inline d-none">
																	Enviar aviso
																</span>
															</>
														)}
													</button>
												</div>
											</form>
										</div>
									</section>

									<div className="mt-4">
										<h4 className=" f-blond mb-0">
											Historial de avisos
										</h4>
									</div>
									<section className="card mb-3 mt-4 card-table">
										<div className="card-body">
											{announcementsLoading ? (
												<p className="txt-loading px-3 py-4 mb-0">
													Cargando avisos...
												</p>
											) : announcements.length === 0 ? (
												<p className="teacher-announcement-empty mb-0">
													Todavia no se han enviado avisos en este curso.
												</p>
											) : (
												<div className="table-responsive mt-3 table-wrapper-scroll">
													<table className="table table-hover align-middle mb-0">
														<thead className="table-light">
															<tr>
																<th><span>Asunto</span></th>
																<th><span>Enviado por</span></th>
																<th className="text-center"><span>Estudiantes</span></th>
																<th><span>Fecha y hora</span></th>
																<th className="text-end"><span>Detalle</span></th>
															</tr>
														</thead>
														<tbody>
															{announcements.map((announcement) => {
																const sentAt = announcement.email_sent_at || announcement.created_at;
																return (
																	<tr key={announcement.id}>
																		<td>
																			<div className="teacher-announcement-table-subject">
																				<span>{capitalizeFirstLetter(
																					announcement.subject || "Aviso",
																				)}</span>
																			</div>
																		</td>
																		<td><span>{capitalizeWords(announcement.sender_name) || "Docente"}</span></td>
																		<td className="text-center"><span>{announcement.recipient_count || 0}</span></td>
																		<td>
																			<span>{sentAt
																				? new Date(sentAt).toLocaleString("es-CL", {
																					day: "2-digit",
																					month: "short",
																					year: "numeric",
																					hour: "2-digit",
																					minute: "2-digit",
																				})
																				: "Sin fecha"}</span>
																		</td>
																		<td className="text-end">
																			<button
																				type="button"
																				className="adm-see-btn"
																				onClick={() => openAnnouncementDetail(announcement)}
																				aria-label={`Ver detalle del aviso: ${announcement.subject || "Aviso"}`}
																				title="Ver detalle"
																			>
																				<i className="ri-eye-fill" aria-hidden="true" />
																			</button>
																		</td>
																	</tr>
																);
															})}
														</tbody>
													</table>
												</div>
											)}
										</div>
									</section>
								</div>
							) : activeTab === "calificaciones" ? (
								<div className="teacher-grades-panel">
									{gradesLoading ? (
										<p className="txt-loading px-3 py-4">
											Cargando calificaciones...
										</p>
									) : (

										<div className="">
											<div className="row g-3 mt-0 student-profile-kpis" style={{ display: "none" }}>
												<div className="col-md-3 mt-3">
													<div className="card card-table shadow-sm lote ">
														<div className="card-body">
															<div>
																<span className="fw-semibold">Nota maxima</span>
															</div>
															<div className="fs-4 fw-semibold">
																<span>{formatGradeStat(gradeStats.max,)}</span>
															</div>
														</div>
													</div>
												</div>
												<div className="col-md-3 mt-3">
													<div className="card card-table shadow-sm important">
														<div className="card-body">
															<div>
																<span className="fw-semibold">Nota minima</span>
															</div>
															<div className="fs-4 fw-semibold">
																<span>{formatGradeStat(gradeStats.min,)}</span>
															</div>
														</div>
													</div>
												</div>
												<div className="col-md-3 mt-3">
													<div className="card card-table shadow-sm certi">
														<div className="card-body">
															<div>
																<span className="fw-semibold">Promedio de notas</span>
															</div>
															<div className="fs-4 fw-semibold">
																<span>{formatGradeStat(gradeStats.avg,)}</span>
															</div>
														</div>
													</div>
												</div>

												<div className="col-md-3 mt-3">
													<div className="card card-table shadow-sm matri">
														<div className="card-body">
															<div>
																<span className="fw-semibold">Desviacion estandar</span>
															</div>
															<div className="fs-4 fw-semibold">
																<span>{formatGradeStat(gradeStats.stdDev,)}</span>
															</div>
														</div>
													</div>
												</div>
											</div>
											<div className="row g-3 mt-4 student-profile-kpis" style={{ display: "none" }}>
												<div className="col-md-6 mt-3">
													<div className="card card-table shadow-sm">
														<div className="card-body">
															<div>
																<span className="fw-semibold">Nro de alumnos que la rindieron</span>
															</div>
															<div className="fs-4 fw-semibold">
																<span>{gradeStats.takenCount}</span>
															</div>
														</div>
													</div>
												</div>
												<div className="col-md-6 mt-3">
													<div className="card card-table shadow-sm">
														<div className="card-body">
															<div>
																<span className="fw-semibold">Nro de alumnos inasistentes</span>
															</div>
															<div className="fs-4 fw-semibold">
																<span>{gradeStats.absentCount}</span>
															</div>
														</div>
													</div>
												</div>
											</div>


											{gradeStats.takenCount > 0 && (
												<section style={{ display: "none" }}>
													<div className="card mb-3 mt-4 card-table">
														<div className="card-body">

															<p className="teacher-grades-chart-title">
																Distribucion de notas
															</p>
															<ResponsiveContainer width="100%" height={220}>
																<BarChart data={gradeChartData.histogram} margin={{ top: 8, right: 12, left: -20, bottom: 0, }}>
																	<CartesianGrid strokeDasharray="3 3" vertical={false} />
																	<XAxis dataKey="label" tick={{ fontSize: 11, }} />
																	<YAxis allowDecimals={false} tick={{ fontSize: 11, }}
																	/>
																	<RechartsTooltip formatter={(value,) => [value, "Alumnos",]} />
																	<Bar dataKey="count" radius={[4, 4, 0, 0,]} >
																		{gradeChartData.histogram.map(
																			(
																				entry,
																				idx,
																			) => (
																				<Cell
																					key={
																						idx
																					}
																					fill={
																						entry.aprobado
																							? "var(--user)"
																							: "var(--logout)"
																					}
																				/>
																			),
																		)}
																	</Bar>
																</BarChart>
															</ResponsiveContainer>
														</div>
													</div>
													<div className="card mb-3 card-table">
														<div className="card-body">
															<p className="teacher-grades-chart-title">
																Aprobados vs
																Reprobados
															</p>
															<ResponsiveContainer
																width="100%"
																height={220}
															>
																<PieChart>
																	<Pie
																		data={
																			gradeChartData.donut
																		}
																		dataKey="value"
																		nameKey="name"
																		cx="50%"
																		cy="50%"
																		innerRadius={
																			55
																		}
																		outerRadius={
																			85
																		}
																		paddingAngle={
																			3
																		}
																	>
																		<Cell fill="var(--user)" />
																		<Cell fill="var(--logout)" />
																	</Pie>
																	<RechartsTooltip
																		formatter={(
																			value,
																		) => [
																				value,
																				"Alumnos",
																			]}
																	/>
																	<Legend
																		iconType="circle"
																		iconSize={
																			10
																		}
																		wrapperStyle={{
																			fontSize: 12,
																		}}
																	/>
																</PieChart>
															</ResponsiveContainer>
														</div>
													</div>
												</section>
											)}

											<div className="teacher-grades-actions">
												<button
													type="button"
													className="btn-excel d-inline-flex align-items-center gap-2"
													onClick={
														handleExportGradesExcel
													}
													disabled={
														gradeExporting ||
														grades.length === 0
													}
												>
													{gradeExporting ? (
														<>
															<span
																className="spinner-border spinner-border-sm"
																role="status"
																aria-hidden="true"
															/>
															<span>
																Exportando...
															</span>
														</>
													) : (
														<>
															<i className="ri-file-excel-2-line" />
															<span className="d-md-inline d-none">
																Exportar
															</span>
														</>
													)}
												</button>
											</div>

											<div className="table-toolbar table-toolbar-locked">
												<div className="toolbar-group toolbar-pagination-group">
													<button
														type="button"
														className="toolbar-nav-btn"
														onClick={() =>
															setGradePage(1)
														}
														disabled={
															gradePage === 1
														}
													>
														&lt;&lt;
													</button>
													<button
														type="button"
														className="toolbar-nav-btn"
														onClick={() =>
															setGradePage((p) =>
																Math.max(
																	1,
																	p - 1,
																),
															)
														}
														disabled={
															gradePage === 1
														}
													>
														&lt;
													</button>
													<select
														className="form-select toolbar-select toolbar-page-select"
														value={gradePage}
														onChange={(e) =>
															setGradePage(
																Number(
																	e.target
																		.value,
																),
															)
														}
														disabled={
															gradeTotalPages <= 1
														}
													>
														{Array.from(
															{
																length: gradeTotalPages,
															},
															(_, i) => i + 1,
														).map((pg) => (
															<option
																key={pg}
																value={pg}
															>
																{pg}
															</option>
														))}
													</select>
													<button
														type="button"
														className="toolbar-nav-btn"
														onClick={() =>
															setGradePage((p) =>
																Math.min(
																	gradeTotalPages,
																	p + 1,
																),
															)
														}
														disabled={
															gradePage >=
															gradeTotalPages
														}
													>
														&gt;
													</button>
													<button
														type="button"
														className="toolbar-nav-btn"
														onClick={() =>
															setGradePage(
																gradeTotalPages,
															)
														}
														disabled={
															gradePage >=
															gradeTotalPages
														}
													>
														&gt;&gt;
													</button>
												</div>

												<div className="search-group toolbar-search-group d-flex align-items-center gap-2">
													<input
														className="form-control"
														value={gradeSearch} onChange={(e) => {
															setGradeSearch(
																e.target.value,
															);
															setGradePage(1);
														}}
														placeholder="Buscar por RUT o nombre"
														maxLength={120}
													/>
													<button
														type="button"
														className="toolbar-nav-btn"
														onClick={() => {
															setGradeSearch("");
															setGradePage(1);
														}}
														disabled={
															!gradeSearch.trim()
														}
														title="Limpiar búsqueda"
														aria-label="Limpiar búsqueda"
													>
														<i className="ri-eraser-line" />
													</button>
												</div>

												<div className="toolbar-group toolbar-controls-group">
													<label className="d-flex align-items-center gap-2 mb-0">
														<span>
															Numero de filas:
														</span>
														<select
															className="form-select toolbar-select"
															value={
																gradeRowsPerPage
															}
															onChange={(e) => {
																setGradeRowsPerPage(
																	Number(
																		e.target
																			.value,
																	),
																);
																setGradePage(1);
															}}
														>
															<option value={10}>
																10
															</option>
															<option value={25}>
																25
															</option>
															<option value={50}>
																50
															</option>
															<option value={100}>
																100
															</option>
														</select>
													</label>
													<label className="d-flex align-items-center gap-2 mb-0">
														<span>
															Ordenar por:
														</span>
														<select
															className="form-select toolbar-select"
															value={
																gradeSortField
															}
															onChange={(e) =>
																setGradeSortField(
																	e.target
																		.value,
																)
															}
														>
															<option value="rut">
																RUT
															</option>
															<option value="full_name">
																Nombre
															</option>
															<option value="final_grade">
																Nota
															</option>
															<option value="exam_score">
																Puntaje
															</option>
															<option value="exam_finished_at">
																Fecha examen
															</option>
														</select>
													</label>
													<button
														type="button"
														className="toolbar-order-btn"
														onClick={() =>
															setGradeSortDir(
																(d) =>
																	d === "asc"
																		? "desc"
																		: "asc",
															)
														}
													>
														{gradeSortDir === "asc"
															? "Asc"
															: "Desc"}
													</button>
												</div>
											</div>

											<div className="table-responsive table-wrapper-scroll">
												<table className="table table-sm table-hover mb-0 align-middle table-organizations-nowrap">
													<thead className="table-light">
														<tr>
															<th>
																<span>RUT</span>
															</th>
															<th>
																<span>
																	Nombre /
																	Apellido(s)
																</span>
															</th>
															<th
																className="grade-cell-center"
																style={{
																	width: "140px",
																}}
															>
																<span>
																	Puntaje
																</span>
															</th>
															<th
																className="grade-cell-center"
																style={{
																	width: "150px",
																}}
															>
																<span>
																	Fecha examen
																</span>
															</th>
															<th
																className="grade-cell-center"
																style={{
																	width: "130px",
																}}
															>
																<span>
																	Nota final
																</span>
															</th>
														</tr>
													</thead>
													<tbody>
														{gradeToShow.length ===
															0 ? (
															<tr>
																<td
																	colSpan={5}
																	className="text-muted p-4"
																>
																	<span>
																		{gradeSearch.trim()
																			? "Sin resultados para la búsqueda."
																			: "No hay calificaciones registradas."}
																	</span>
																</td>
															</tr>
														) : (
															gradeToShow.map(
																(g) => (
																	<tr
																		key={
																			g.enrollment_id
																		}
																	>
																		<td>
																			<span>
																				{highlightRutSearchMatch(
																					g.rut
																						? formatRut(
																							g.rut,
																						)
																						: "-",
																					gradeSearch,
																				)}
																			</span>
																		</td>
																		<td>
																			<span>
																				{highlightSearchMatch(
																					capitalizeWords(
																						g.full_name,
																					),
																					gradeSearch,
																				)}
																			</span>
																		</td>
																		<td className="grade-cell-center">
																			<span>
																				{g.exam_score !== null &&
																					g.exam_score !== undefined
																					? `${Number(g.exam_score).toFixed(1)}%`
																					: "-"}
																			</span>
																		</td>
																		<td className="grade-cell-center">
																			<span>
																				{g.exam_finished_at
																					? formatDate(g.exam_finished_at)
																					: "-"}
																			</span>
																		</td>
																		<td className="grade-cell-center">
																			<span
																				className={`badge ${Number(g.final_grade) >= 4.0 ? "adm-save-btn" : "adm-del-btn"} fs-6`}
																			>
																				{g.final_grade !== null &&
																					g.final_grade !== undefined
																					? Number(g.final_grade).toFixed(1)
																					: "-"}
																			</span>
																		</td>
																	</tr>
																),
															)
														)}
														<tr
															className="table-light"
															style={{
																fontWeight:
																	"bold",
															}}
														>
															<td colSpan={4}>
																<span>
																	Promedio
																	General del
																	Curso
																</span>
															</td>
															<td className="grade-cell-center">
																<span>
																	{calculatedGradesAverage !==
																		null
																		? calculatedGradesAverage.toFixed(
																			1,
																		)
																		: "-"}
																</span>
															</td>
														</tr>
													</tbody>
												</table>
											</div>
											<div className="table-footer-summary">
												<span>
													Mostrando{" "}
													{gradeSorted.length === 0
														? 0
														: gradeStart + 1}{" "}
													- {gradeEnd} de{" "}
													{gradeSorted.length}{" "}
													registros
												</span>
											</div>
										</div>
									)}
								</div>
							) : activeTab === "examen" ? (
								<TeacherExamTab runId={runId} />
							) : (
								<>
									<div className="div-contenido-doc">
										<h2 className="aula-section-title mb-0">Contenido</h2>
										<div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
											<button
												type="button"
												className="admin-btn text-nowrap"
												onClick={toggleAllUnits}
												disabled={(run.units || []).length === 0}
												title={
													allUnitsExpanded
														? "Colapsar todas las unidades"
														: "Expandir todas las unidades"
												}
												aria-label={
													allUnitsExpanded
														? "Colapsar todas las unidades"
														: "Expandir todas las unidades"
												}
											>
												<i
													className={
														allUnitsExpanded
															? "ri-contract-line"
															: "ri-expand-diagonal-line"
													}
												/>
												<span className="d-none d-md-inline">
													{allUnitsExpanded ? "Colapsar todo" : "Expandir todo"}
												</span>
											</button>
											<button
												type="button"
												className="admin-btn text-nowrap"
												onClick={() =>
													setModal({ type: "addUnit" })
												}
											>
												<i className="ri-add-line"></i>
												<span className="d-none d-md-inline ">
													Agregar unidad
												</span>
											</button>
										</div>
									</div>

									{(run.units || []).map((unit) => (
										<div
											key={unit.id}
											className="aula-unit "
										>
											<div
												className="aula-unit-header aula-unit-header-doc "
												onClick={() =>
													toggleUnit(unit.id)
												}
											>
												<h3 className="aula-unit-title">
													{capitalizeWords(
														unit.title,
													)}
													<i
														className={`ri-arrow-down-s-line ms-2 ${expandedUnits[unit.id] ? "rotate-180" : ""}`}
														style={{
															transition:
																"transform 0.2s",
														}}
													/>
												</h3>
												<div
													className="d-flex gap-2"
													onClick={(e) =>
														e.stopPropagation()
													}
												>
													<button
														type="button"
														className="adm-upd-btn teacher-action-icon-btn"
														onClick={() =>
															setModal({
																type: "editUnit",
																unit,
															})
														}
														title="Editar"
														aria-label="Editar unidad"
													>
														<i
															className="ri-edit-fill"
															aria-hidden
														/>
													</button>
													<button
														type="button"
														className="adm-see-btn teacher-action-icon-btn"
														onClick={() =>
															setModal({
																type: "addWeek",
																unit,
															})
														}
														title="Agregar semana"
														aria-label="Agregar semana"
													>
														<i
															className="ri-calendar-event-fill"
															aria-hidden
														/>
													</button>
													<button
														type="button"
														className="adm-save-btn teacher-action-icon-btn"
														onClick={() =>
															setModal({
																type: "uploadFormativeExam",
																unit,
															})
														}
														title="Subir Evaluación Formativa"
														aria-label="Subir Evaluación Formativa"
													>
														<i
															className="ri-file-text-fill"
															aria-hidden
														/>
													</button>
													<button
														type="button"
														className="adm-del-btn teacher-action-icon-btn"
														onClick={() =>
															setModal({
																type: "delete",
																name: unit.title,
																onConfirm: () =>
																	handleDeleteUnit(
																		unit.id,
																	),
															})
														}
														title="Eliminar unidad"
														aria-label="Eliminar unidad"
													>
														<i
															className="ri-delete-bin-line"
															aria-hidden
														/>
													</button>
												</div>
											</div>
											{expandedUnits[unit.id] !==
												false && (
													<div className="aula-unit-body">
														{(unit.modules || []).map(
															(mod) => {
																const activeCategoryForModule =
																	"all";
																const addResourceCategory =
																	"material";
																return (
																	<div
																		key={mod.id}
																		className="aula-week-row"
																	>
																		<div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
																			<div className="aula-week-label mb-0">
																				<span>
																					{capitalizeFirstLetter(
																						mod.title,
																					)}
																				</span>
																			</div>
																			<div className="d-flex gap-2 ">
																				<button
																					type="button"
																					className="adm-upd-btn teacher-action-icon-btn"
																					onClick={() =>
																						setModal(
																							{
																								type: "editWeek",
																								module: mod,
																							},
																						)
																					}
																					title="Editar semana"
																					aria-label="Editar semana"
																				>
																					<i
																						className="ri-edit-fill"
																						aria-hidden
																					/>
																				</button>
																				<button
																					type="button"
																					className="adm-del-btn teacher-action-icon-btn"
																					onClick={() =>
																						setModal(
																							{
																								type: "delete",
																								name: mod.title,
																								onConfirm:
																									() =>
																										handleDeleteWeek(
																											mod.id,
																										),
																							},
																						)
																					}
																					title="Eliminar semana"
																					aria-label="Eliminar semana"
																				>
																					<i
																						className="ri-delete-bin-line"
																						aria-hidden
																					/>
																				</button>
																			</div>
																		</div>
																		<div className="aula-category-buttons">
																			{activeCategoryForModule &&
																				(() => {
																					const cat =
																						activeCategoryForModule;
																					const baseItems =
																						flattenModuleContents(mod.contents);
																					const items = getVisibleResources(
																						mod.id,
																						cat,
																						baseItems,
																					);
																					return (
																						<div className="aula-cat-panel">
																							{items.length ===
																								0 ? (
																								<p className="text-muted small mb-0">
																									Sin contenido en esta semana.
																								</p>
																							) : (
																								<ul
																									className="aula-cat-list"
																									onDragOver={handleResourceListDragOver}
																									onDrop={handleResourceListDrop}
																								>
																									{items.map(
																										(
																											c,
																											index,
																										) => {
																											const iconClass =
																												c.file
																													? getFileIconClass(
																														c.file_name ||
																														c.file,
																													)
																													: c.content_type === "video"
																														? "ri-video-line"
																														: c.url
																															? "ri-links-line"
																															: "ri-file-text-fill";
																											const label =
																												c.title ||
																												(c.file
																													? "Archivo"
																													: "Recurso");
																											const isCurrentDragTarget =
																												dragOverResource?.moduleId === mod.id &&
																												dragOverResource?.category === cat &&
																												dragOverResource?.index === index;
																											const isSameItemAsDragged =
																												(draggedResource?.moduleId === mod.id &&
																													draggedResource?.category === cat &&
																													draggedResource?.index === index) ||
																												(dragOverResource?.sourceModuleId === mod.id &&
																													dragOverResource?.sourceCategory === cat &&
																													dragOverResource?.sourceIndex === index);
																											const isSwapTarget =
																												isCurrentDragTarget &&
																												(!isSameItemAsDragged || !draggedResource);
																											return (
																												<li
																													key={
																														c.id
																													}
																													className={`aula-content-item responsive-item ${isCurrentDragTarget ? "is-drag-over" : ""} ${isCurrentDragTarget && dragOverResource?.position === "before" ? "is-drag-over-before" : ""} ${isCurrentDragTarget && dragOverResource?.position === "after" ? "is-drag-over-after" : ""} ${isSwapTarget ? "is-swap-target" : ""}`}
																													draggable={!resourceReordering}
																													onMouseDown={handleResourceItemMouseDown}
																													onDragStart={(event) =>
																														handleResourceItemDragStart(
																															event,
																															mod.id,
																															cat,
																															index,
																														)
																													}
																													onDragEnd={handleResourceDragEnd}
																													onDragOver={(event) =>
																														handleResourceDragOver(
																															event,
																															mod.id,
																															cat,
																															index,
																														)
																													}
																													onDragEnter={(event) =>
																														handleResourceDragOver(
																															event,
																															mod.id,
																															cat,
																															index,
																														)
																													}
																													onDrop={(event) =>
																														handleResourceDrop(
																															event,
																															mod.id,
																															cat,
																															index,
																															dragOverResource?.moduleId ===
																																mod.id &&
																																dragOverResource?.category ===
																																cat &&
																																dragOverResource?.index ===
																																index
																																? dragOverResource?.position
																																: "before",
																															baseItems,
																														)
																													}
																												>
																													<div className="item-header">
																														<button
																															type="button"
																															className="aula-resource-drag-handle"
																															title="Manten presionado y arrastra para reordenar"
																															aria-label="Mover recurso"
																															disabled={resourceReordering}
																														>
																															<i
																																className="ri-draggable aula-resource-drag-handle-icon"
																																aria-hidden
																															/>
																														</button>
																														<i
																															className={`${iconClass} aula-content-icon`}
																															aria-hidden
																														/>
																														<span className="aula-content-label">
																															{capitalizeFirstLetter(
																																label,
																															)}
																														</span>
																													</div>
																													<div className="aula-resource-links d-flex align-items-center gap-2">
																														{[
																															c.url &&
																															!c.text_content &&
																															c.content_type !==
																															"image" && (
																																<a
																																	key="url"
																																	href={
																																		c.url
																																	}
																																	target="_blank"
																																	rel="noreferrer"
																																	className="adm-see-btn"
																																	title="Ver enlace"
																																	aria-label="Ver enlace"
																																>
																																	<i className="ri-external-link-line" aria-hidden />
																																</a>
																															),
																															c.file &&
																															c.content_type !==
																															"image" && (
																																<a
																																	key="file"
																																	href={
																																		c.file
																																	}
																																	target="_blank"
																																	rel="noreferrer"
																																	download={
																																		c.file_name ||
																																		undefined
																																	}
																																	className="adm-see-btn"
																																	title="Descargar"
																																	aria-label="Descargar"
																																>
																																	<i className="ri-download-2-line" aria-hidden />
																																</a>
																															),
																															(c.text_content ||
																																c.content_type ===
																																"image" ||
																																(c.url &&
																																	c.text_content)) && (
																																<button
																																	key="detail"
																																	type="button"
																																	className="adm-see-btn"
																																	onClick={() =>
																																		setModal(
																																			{
																																				type: "viewResource",
																																				content:
																																					c,
																																			},
																																		)
																																	}
																																	title="Ver detalle"
																																	aria-label="Ver detalle"
																																>
																																	<i className="ri-eye-fill" aria-hidden />
																																</button>
																															),
																														]
																															.filter(
																																Boolean,
																															)}
																														{!c.url &&
																															!c.file &&
																															!c.text_content && (
																																<span className="text-muted small">
																																	Sin
																																	contenido
																																</span>
																															)}
																													</div>
																													<div className="admin-actions-group">
																														<button
																															type="button"
																															className="adm-upd-btn teacher-action-icon-btn"
																															onClick={() =>
																																setModal(
																																	{
																																		type: "editResource",
																																		content:
																																			c,
																																		module: mod,
																																	},
																																)
																															}
																															title="Editar"
																															aria-label="Editar"
																														>
																															<i className="ri-edit-fill" aria-hidden />
																														</button>
																														<button
																															type="button"
																															className="adm-del-btn teacher-action-icon-btn"
																															onClick={() =>
																																setModal(
																																	{
																																		type: "delete",
																																		name: c.title,
																																		onConfirm:
																																			() =>
																																				handleDeleteResource(
																																					c.id,
																																				),
																																	},
																																)
																															}
																															title="Eliminar"
																															aria-label="Eliminar"
																														>
																															<i className="ri-delete-bin-line" aria-hidden />
																														</button>
																													</div>
																												</li>
																											);
																										},
																									)}
																								</ul>
																							)}
																							<div className="d-flex justify-content-center mt-3 pt-3 border-top">
																								<button
																									type="button"
																									className="add-doc aula-add-resource-btn"
																									onClick={() =>
																										setModal({
																											type: "addResourcePicker",
																											module: mod,
																											category: addResourceCategory,
																										})
																									}
																									title="Anadir una actividad o un recurso"
																								>
																									<i className="ri-add-line" aria-hidden />
																									<span>Anadir una actividad o un recurso</span>
																								</button>
																							</div>
																						</div>
																					);
																				})()}
																		</div>
																	</div>
																);
															},
														)}

														{(unit.formative_exams || []).map((exam) => (
															<div key={exam.id} className="aula-week-row mt-4">
																<div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
																	<div className="aula-week-label mb-0">
																		<span>Evaluación Formativa</span>
																	</div>
																</div>
																<div className="card card-table shadow-sm">
																	<div className="card-body p-4">
																		<div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
																			<div>
																				<h5 className="mb-1">Prueba de Práctica</h5>
																				<p className="mb-0 text-muted small">Aprobación: {exam.passing_score}% • Intentos: {exam.max_attempts === 0 ? 'Ilimitados' : exam.max_attempts}</p>
																			</div>
																			<span className="status status-ok">Configurada</span>
																		</div>
																	</div>
																</div>
															</div>
														))}
													</div>
												)}
										</div>
									))}

									{(!run.units || run.units.length === 0) && (
										<div className="text-muted py-4 text-center">
											<span>
												No hay unidades. Usa "Agregar
												unidad" para crear la primera.
											</span>
										</div>
									)}
								</>
							)}
						</div>
					</div>

					<aside className="aula-sidebar">
						<button
							type="button"
							className="aula-card course-messages-trigger"
							onClick={() => setMessagesDrawerOpen(true)}
							aria-label="Abrir mensajes del curso"
						>
							<div className="course-messages-trigger-inner">
								<div className="d-flex fd-column gap-2 align-items-center">
									<i
										className="ri-chat-3-line course-messages-trigger-icon"
										aria-hidden
									/>
									<h5
										className="aula-card-title text-nowrap mb-0"
										style={{ marginBottom: 0 }}
									>
										Mensajes del curso
									</h5>
								</div>
								<p className="course-messages-trigger-hint text-muted">
									Haz clic para abrir la conversación
								</p>
							</div>
						</button>
						<CourseMessagesDrawer
							open={messagesDrawerOpen}
							onClose={() => setMessagesDrawerOpen(false)}
							runId={runId}
							courseName={courseName}
						/>
					</aside>
				</div >

				{/* Modals */}
				{
					modal?.type === "addUnit" && (
						<ModalForm
							title="Nueva unidad"
							initial={{ title: "" }}
							fields={[
								{ name: "title", label: "Titulo", required: true },
							]}
							onSubmit={(data) => handleAddUnit(data.title)}
							onClose={() => setModal(null)}
							saving={saving}
						/>
					)
				}
				{
					modal?.type === "editUnit" && (
						<ModalForm
							title="Editar unidad"
							initial={{ title: modal.unit?.title || "" }}
							fields={[
								{ name: "title", label: "Titulo", required: true },
							]}
							onSubmit={(data) =>
								handleEditUnit(modal.unit.id, data.title)
							}
							onClose={() => setModal(null)}
							saving={saving}
						/>
					)
				}
				{
					modal?.type === "uploadFormativeExam" && (
						<ModalForm
							title="Subir Evaluación Formativa"
							initial={{ file: null }}
							fields={[
								{ name: "file", label: "Archivo Aiken (.txt)", required: true, type: "file", accept: ".txt" },
							]}
							onSubmit={(data) =>
								handleUploadFormativeExam(modal.unit?.id, data.file)
							}
							onClose={() => setModal(null)}
							saving={saving}
						/>
					)
				}
				{
					modal?.type === "addWeek" && (
						<ModalForm
							title="Nueva semana"
							initial={{ title: "" }}
							fields={[
								{
									name: "title",
									label: "Titulo (ej: Primera Semana)",
									required: false,
								},
							]}
							onSubmit={(data) =>
								handleAddWeek(modal.unit?.id, data.title)
							}
							onClose={() => setModal(null)}
							saving={saving}
						/>
					)
				}
				{
					modal?.type === "editWeek" && (
						<ModalForm
							title="Editar semana"
							initial={{ title: modal.module?.title || "" }}
							fields={[
								{ name: "title", label: "Titulo", required: true },
							]}
							onSubmit={(data) =>
								handleEditWeek(modal.module?.id, data.title)
							}
							onClose={() => setModal(null)}
							saving={saving}
						/>
					)
				}
				{
					modal?.type === "addResource" && (
						<ResourceForm
							title="Nuevo recurso"
							category={modal.category}
							pickerItemId={modal.pickerItemId}
							initial={modal.initial}
							onSubmit={(payload, file) =>
								handleAddResource(modal.module?.id, payload, file)
							}
							onClose={() => setModal(null)}
							saving={saving}
						/>
					)
				}
				{
					modal?.type === "addResourcePicker" && (
						<ResourcePickerModal
							initialCategory={modal.category || "material"}
							onClose={() => setModal(null)}
							onSelect={(item) =>
								setModal({
									type: "addResource",
									module: modal.module,
									category: "material",
									initial: {
										content_type: item.contentType,
										url: item.id === "url" ? "https://" : "",
									},
								})
							}
						/>
					)
				}
				{
					modal?.type === "editResource" && (
						<ResourceForm
							title="Editar recurso"
							initial={modal.content}
							onSubmit={(payload, file) =>
								handleEditResource(modal.content?.id, payload, file)
							}
							onClose={() => setModal(null)}
							saving={saving}
						/>
					)
				}
				{
					modal?.type === "delete" && (
						<ModalConfirm
							title="Estas seguro?"
							message={
								<>
									Esta acción no se puede deshacer. Vas a
									eliminar:{" "}
									<strong>{modal.name || "este elemento"}</strong>
									.
								</>
							}
							onConfirm={modal.onConfirm}
							onClose={() => setModal(null)}
							saving={saving}
						/>
					)
				}
				{
					modal?.type === "viewResource" && (
						<Modal
							title={modal.content?.title || "Detalle del recurso"}
							open={true}
							onClose={() => setModal(null)}
							footer={
								<button
									className="adm-del-btn"
									onClick={() => setModal(null)}
								>
									<span>Cerrar</span>
								</button>
							}
						>
							<div className="font-n">
								{/* Si el docente escribio un texto */}
								{modal.content?.text_content && (
									<div className="mb-4">
										<span>Contenido:</span>
										<div
											className="p-3 mt-2 card-table shadow-none rte-wrapper"
											dangerouslySetInnerHTML={{
												__html: DOMPurify.sanitize(
													modal.content.text_content
												)
											}}
										/>
									</div>
								)}

								{/* Si el docente adjunto un enlace */}
								{modal.content?.url && (
									<div className="mb-4">
										<span>Enlace adjunto:</span>
										<div>
											<a
												href={modal.content.url}
												target="_blank"
												rel="noreferrer"
												className="text-primary text-break"
												style={{
													textDecoration: "underline",
												}}
											>
												{modal.content.url}
											</a>
										</div>
									</div>
								)}

								{/* Si el recurso es una imagen y tiene archivo adjunto */}
								{modal.content?.file &&
									modal.content?.content_type === "image" && (
										<div className="mb-3 text-left">
											<span>Imagen adjunta:</span>
											<div className="modal-img-placeholder">
												<img
													src={modal.content.file}
													alt={
														modal.content.title ||
														"Imagen del recurso"
													}
													className="shadow-sm mt-2"
													style={{
														maxWidth: "100%",
														maxHeight: "500px",
														objectFit: "contain",
														borderRadius: "8px",
														border: "1px solid var(--dark-blue",
													}}
												/>
											</div>
										</div>
									)}
							</div>
						</Modal>
					)
				}
				<RecordingPlayerModal
					recording={selectedRecording}
					onClose={() => setSelectedRecording(null)}
				/>
				<Modal
					title={selectedAnnouncement?.subject || "Detalle del aviso"}
					open={Boolean(selectedAnnouncement)}
					onClose={closeAnnouncementDetail}
					dialogClassName="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg"
					footer={
						<button
							type="button"
							className="adm-del-btn"
							onClick={closeAnnouncementDetail}
						>
							<span>Cerrar</span>
						</button>
					}
				>
					{selectedAnnouncement && (
						<div className="teacher-announcement-detail-modal font-n">
							<div>
								<div className="row-info">
									<p>Enviado por:</p>
									<span>{capitalizeWords(selectedAnnouncement.sender_name) || "Docente"}</span>
								</div>
								<div className="row-info mt-2">
									<p>Estudiantes notificados:</p>
									<span>{selectedAnnouncement.recipient_count || 0}</span>
								</div>
								<div className="row-info mt-2">
									<p>Fecha de envio:</p>
									<span>
										{selectedAnnouncementSentAt
											? new Date(selectedAnnouncementSentAt).toLocaleString("es-CL", {
												day: "2-digit",
												month: "long",
												year: "numeric",
												hour: "2-digit",
												minute: "2-digit",
											})
											: "Sin fecha"}
									</span></div>
							</div>
							<div className="teacher-announcement-detail-body">
								<div className=""><span>Mensaje:</span></div>
								<div
									className="p-3 mt-2 card-table shadow-none"
									dangerouslySetInnerHTML={{
										__html: DOMPurify.sanitize(selectedAnnouncement.body_html || "", {
											USE_PROFILES: { html: true },
											FORBID_TAGS: ["style", "script", "iframe", "object", "embed"],
											FORBID_ATTR: ["style", "onerror", "onload", "onclick"],
										}),
									}}
								/>
							</div>
						</div>
					)}
				</Modal>
			</div >
			<Modal
				title="Detalles del Curso"
				open={isInfoModalOpen}
				onClose={() => setIsInfoModalOpen(false)}
				dialogClassName="modal-dialog modal-dialog-centered modal-lg"
				footer={
					<button
						className="adm-del-btn"
						onClick={() => setIsInfoModalOpen(false)}
					>
						<span>Cerrar</span>
					</button>
				}
			>
				<div className="font-n">
					<h2 className="mb-4">
						{capitalizeFirstLetter(run.name || "")}
					</h2>
					<div className="desc-first">
						<hr className="course-divider-line" />
						<div>
							<div className="row-info">
								<p>Horario de Clases:</p>
								<span>
									{formatWeeklyScheduleForDisplay(
										run.schedule,
									) || "Sin horario definido"}
								</span>
							</div>
							<div className="row-info mt-3">
								<p>Modalidad:</p>
								<span>{traducirModalidad(run.modality)}</span>
							</div>
						</div>
					</div>
					<div className="desc-description me-4">
						<hr className="course-divider-line" />
						<div>
							<p className="font-tittle-desc">Descripción</p>
							<div>
								{tieneContenido(run.description) ? (
									<div
										className="valores-body-text"
										dangerouslySetInnerHTML={{
											__html: DOMPurify.sanitize(run.description),
										}}
									/>
								) : (
									<p className="text-muted">
										Sin descripción disponible.
									</p>
								)}
							</div>
						</div>
					</div>

					<div className="desc-description me-4">
						<hr className="course-divider-line" />
						<div>
							<p className="font-tittle-desc">Objetivos</p>
							<div>
								{tieneContenido(run.objectives) ? (
									<div
										className="valores-body-text"
										dangerouslySetInnerHTML={{
											__html: DOMPurify.sanitize(run.objectives),
										}}
									/>
								) : (
									<p className="text-muted">
										Sin objetivos definidos.
									</p>
								)}
							</div>
						</div>
					</div>

					<div className="desc-description me-4">
						<hr className="course-divider-line" />
						<div>
							<p className="font-tittle-desc">
								Temario / Contenidos
							</p>
							<div>
								{tieneContenido(run.temario) ? (
									<div
										className="valores-body-text"
										dangerouslySetInnerHTML={{
											__html: DOMPurify.sanitize(run.temario),
										}}
									/>
								) : (
									<p className="text-muted">
										Temario en desarrollo.
									</p>
								)}
							</div>
						</div>
					</div>

					<div className="desc-description me-4">
						<hr className="course-divider-line" />
						<div>
							<p className="font-tittle-desc">
								Requisitos de Graduación
							</p>
							<div>
								{tieneContenido(run.graduation_requirements) ? (
									<span>{run.graduation_requirements}</span>
								) : (
									<p className="text-muted">
										Temario en desarrollo.
									</p>
								)}
							</div>
						</div>
					</div>
				</div>
			</Modal>
		</>
	);
}

function ModalForm({ title, initial, fields, onSubmit, onClose, saving }) {
	const [form, setForm] = useState(initial || {});
	const [fieldErrors, setFieldErrors] = useState({});

	const handleSubmit = (e) => {
		e.preventDefault();
		if (saving) return;
		const err = {};
		fields.forEach((f) => {
			if (f.required) {
				const val = form[f.name];
				if (f.type === "file" && !val) {
					err[f.name] = "Este archivo es obligatorio.";
				} else if (f.type !== "file" && !(val ?? "").toString().trim()) {
					err[f.name] = "Este campo es obligatorio.";
				}
			}
		});
		setFieldErrors(err);
		if (Object.keys(err).length > 0) return;
		onSubmit(form);
	};

	const handleBackdrop = (e) => {
		if (e.target === e.currentTarget) onClose();
	};

	const handleChange = (name, value) => {
		setForm((p) => ({ ...p, [name]: value }));
		if (fieldErrors[name])
			setFieldErrors((p) => ({ ...p, [name]: undefined }));
	};

	return (
		<div
			className="modal show d-block"
			style={{ background: "rgba(0,0,0,0.5)" }}
			tabIndex={-1}
			onClick={handleBackdrop}
			role="dialog"
			aria-modal="true"
			aria-labelledby="modal-title"
		>
			<div
				className="modal-dialog modal-dialog-centered"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="modal-content">
					<div className="modal-header">
						<h5 id="modal-title" className="modal-title">
							{capitalizeFirstLetter(title)}
						</h5>
						<button
							type="button"
							className="btn-close"
							onClick={onClose}
							aria-label="Cerrar"
						/>
					</div>
					<form onSubmit={handleSubmit}>
						<div className="modal-body">
							{fields.map((f) => (
								<div key={f.name} className="mb-3">
									<label
										className="form-label"
										htmlFor={`modal-field-${f.name}`}
									>
										<span>
											{capitalizeFirstLetter(f.label)}
										</span>
									</label>
									<input
										id={`modal-field-${f.name}`}
										type={f.type || "text"}
										className={`form-control ${fieldErrors[f.name] ? "is-invalid" : ""}`}
										value={f.type === "file" ? undefined : (form[f.name] ?? "")}
										placeholder="Ingresa un valor"
										onChange={(e) => {
											if (f.type === "file") {
												handleChange(f.name, e.target.files[0]);
											} else {
												handleChange(f.name, e.target.value);
											}
										}}
										accept={f.accept}
										required={f.required}
										autoComplete="off"
										aria-invalid={!!fieldErrors[f.name]}
									/>
									{fieldErrors[f.name] && (
										<div className="invalid-feedback">
											<span>
												{capitalizeFirstLetter(
													fieldErrors[f.name],
												)}
											</span>
										</div>
									)}
								</div>
							))}
						</div>
						<div className="modal-footer">
							<button
								type="button"
								className="adm-del-btn"
								onClick={onClose}
								disabled={saving}
							>
								<span>Cancelar</span>
							</button>
							<button
								type="submit"
								className="adm-save-btn"
								disabled={saving}
							>
								<span>
									{saving ? "Guardando..." : "Guardar"}
								</span>
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
}

function ResourcePickerModal({
	onClose,
	onSelect,
	initialCategory = "material",
}) {
	const [query, setQuery] = useState("");
	const [activeTab, setActiveTab] = useState("featured");
	const [detailItemId, setDetailItemId] = useState(null);
	const [favoriteIds, setFavoriteIds] = useState(() => {
		try {
			const raw = window.localStorage.getItem(
				"teacher_resource_picker_favorites",
			);
			const parsed = raw ? JSON.parse(raw) : [];
			return Array.isArray(parsed)
				? parsed.filter((id) =>
					RESOURCE_PICKER_OPTIONS.some((opt) => opt.id === id),
				)
				: [];
		} catch {
			return [];
		}
	});
	const selectedCategory = initialCategory || "material";

	useEffect(() => {
		try {
			window.localStorage.setItem(
				"teacher_resource_picker_favorites",
				JSON.stringify(favoriteIds),
			);
		} catch {
			// Ignore storage errors (private mode/quota).
		}
	}, [favoriteIds]);

	const detailItem = useMemo(
		() =>
			RESOURCE_PICKER_OPTIONS.find((item) => item.id === detailItemId) ||
			null,
		[detailItemId],
	);

	const filteredItems = useMemo(() => {
		const normalizedQuery = query.trim().toLowerCase();
		return RESOURCE_PICKER_OPTIONS.filter((item) => {
			const byQuery =
				normalizedQuery.length === 0 ||
				item.label.toLowerCase().includes(normalizedQuery);
			if (!byQuery) return false;

			if (activeTab === "featured") {
				return favoriteIds.includes(item.id);
			}
			if (activeTab === "activities") {
				return item.type === "activity";
			}
			if (activeTab === "resources") {
				return item.type === "resource";
			}

			return true;
		});
	}, [query, activeTab, favoriteIds]);

	const toggleFavorite = (itemId) => {
		setFavoriteIds((prev) =>
			prev.includes(itemId)
				? prev.filter((id) => id !== itemId)
				: [...prev, itemId],
		);
	};

	const handleBackdrop = (e) => {
		if (e.target === e.currentTarget) onClose();
	};

	return (
		<div
			className="modal show d-block"
			style={{ background: "rgba(0,0,0,0.5)" }}
			tabIndex={-1}
			onClick={handleBackdrop}
			role="dialog"
			aria-modal="true"
			aria-labelledby="resource-picker-modal-title"
		>
			<div
				className="modal-dialog modal-dialog-centered modal-xl"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="modal-content teacher-resource-picker-modal">
					<div className="modal-header">
						<h5
							id="resource-picker-modal-title"
							className="modal-title"
						>
							Anadir una actividad o un recurso
						</h5>
						<button
							type="button"
							className="btn-close"
							onClick={onClose}
							aria-label="Cerrar"
						/>
					</div>
					<div className="modal-body">
						{!detailItem ? (
							<>
								<div className="teacher-resource-picker-search mb-2">
									<input
										type="search"
										className="form-control"
										placeholder="Buscar"
										value={query}
										onChange={(e) => setQuery(e.target.value)}
									/>
								</div>

								<div className="teacher-resource-picker-tabs mb-3">
									{[
										{ id: "featured", label: "Destacados" },
										{ id: "all", label: "Todos" },
										{ id: "activities", label: "Actividades" },
										{ id: "resources", label: "Recursos" },
									].map((tab) => (
										<button
											key={tab.id}
											type="button"
											className={`teacher-resource-picker-tab ${activeTab === tab.id ? "active" : ""}`}
											onClick={() => setActiveTab(tab.id)}
										>
											{tab.label}
										</button>
									))}
								</div>

								<div className="teacher-resource-picker-grid">
									{filteredItems.length === 0 ? (
										<div className="teacher-resource-picker-empty">
											{activeTab === "featured"
												? "No hay elementos destacados. Marca una estrella para añadirlos aquí."
												: "No hay opciones para esta búsqueda."}
										</div>
									) : (
										filteredItems.map((item) => {
											const isFavorite = favoriteIds.includes(item.id);
											return (
												<div
													key={item.id}
													className={`teacher-resource-picker-card ${item.enabled ? "" : "is-disabled"}`}
													onClick={() =>
														item.enabled &&
														onSelect(item, selectedCategory)
													}
													style={{ cursor: item.enabled ? 'pointer' : 'default' }}
												>
													<div className="teacher-resource-picker-card-actions">
														<button
															type="button"
															className={`teacher-resource-picker-star ${isFavorite ? "is-favorite" : ""}`}
															onClick={(e) => {
																e.preventDefault();
																e.stopPropagation();
																toggleFavorite(item.id);
															}}
															title={isFavorite ? "Quitar de destacados" : "Agregar a destacados"}
															aria-label={isFavorite ? "Quitar de destacados" : "Agregar a destacados"}
														>
															<i className={`${isFavorite ? "ri-star-fill" : "ri-star-line"}`} aria-hidden />
														</button>
														<button
															type="button"
															className="teacher-resource-picker-info"
															onClick={(e) => {
																e.preventDefault();
																e.stopPropagation();
																setDetailItemId(item.id);
															}}
															title="Ver descripción"
															aria-label="Ver descripción"
														>
															<i className="ri-information-fill" aria-hidden />
														</button>
													</div>
													<i
														className={`${item.icon} teacher-resource-picker-card-icon`}
														aria-hidden
													/>
													<span className="teacher-resource-picker-card-title">
														{item.label}
													</span>
												</div>
											);
										})
									)}
								</div>
							</>
						) : (
							<div className="teacher-resource-picker-detail">
								<div className="teacher-resource-picker-detail-title">
									<i className={detailItem.icon} aria-hidden />
									<span>{detailItem.label}</span>
								</div>
								<div className="teacher-resource-picker-detail-body">
									{(detailItem.description || []).map((paragraph, idx) => (
										<p key={`${detailItem.id}-desc-${idx}`}>{paragraph}</p>
									))}
									{detailItem.helpUrl && (
										<a
											href={detailItem.helpUrl}
											target="_blank"
											rel="noreferrer"
											className="teacher-resource-picker-help-link"
										>
											<i className="ri-article-line" aria-hidden /> Mas ayuda
											<i className="ri-external-link-line" aria-hidden />
										</a>
									)}
								</div>
								<div className="teacher-resource-picker-detail-footer">
									<button
										type="button"
										className="btn btn-secondary"
										onClick={() => setDetailItemId(null)}
									>
										Volver
									</button>
									<button
										type="button"
										className="adm-save-btn"
										onClick={() =>
											detailItem.enabled &&
											onSelect(detailItem, selectedCategory)
										}
										disabled={!detailItem.enabled}
									>
										Anadir
									</button>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

function FileDropZone({
	file,
	existingName,
	existingUrl,
	accept,
	onFileChange,
}) {
	const inputRef = useRef(null);
	const [dragging, setDragging] = useState(false);
	const [popupOpen, setPopupOpen] = useState(false);

	const hasStaged = !!file;
	const hasExisting = !!existingName && !hasStaged;

	const handleDragOver = (e) => {
		e.preventDefault();
		setDragging(true);
	};
	const handleDragLeave = () => setDragging(false);
	const handleDrop = (e) => {
		e.preventDefault();
		setDragging(false);
		const dropped = e.dataTransfer.files?.[0];
		if (dropped) onFileChange(dropped);
	};
	const handleInputChange = (e) => {
		if (e.target.files?.[0]) onFileChange(e.target.files[0]);
	};

	if (hasStaged || hasExisting) {
		const displayName = file ? file.name : existingName;
		const sizeLabel = file
			? file.size >= 1048576
				? `${(file.size / 1048576).toFixed(1)} MB`
				: `${(file.size / 1024).toFixed(0)} KB`
			: null;
		const iconClass = getFileIconClass
			? getFileIconClass(displayName)
			: "ri-file-line";

		return (
			<div className="resource-file-manager">
				<div className="resource-file-grid">
					<button
						type="button"
						className="resource-file-card"
						onClick={() => setPopupOpen(true)}
						title="Haz clic para ver opciones"
					>
						<i
							className={`${iconClass} resource-file-card-icon`}
							aria-hidden
						/>
						<span className="resource-file-card-name">
							{displayName}
						</span>
						{sizeLabel && (
							<span className="resource-file-card-size">
								{sizeLabel}
							</span>
						)}
					</button>
				</div>
				{popupOpen && (
					<div
						className="resource-file-popup-backdrop"
						onClick={() => setPopupOpen(false)}
					>

						<div
							className="resource-file-popup"
							onClick={(e) => e.stopPropagation()}
						>
							<div className="resource-file-popup-header">
								<div>
									<span className="resource-file-popup-title">
										{displayName}
									</span>
								</div>

								<button
									type="button"
									className="btn-close"
									onClick={() => setPopupOpen(false)}
									aria-label="Cerrar"
								/>
							</div>
							<div className="resource-file-popup-actions">
								{existingUrl && hasExisting && (
									<a
										href={existingUrl}
										target="_blank"
										rel="noopener noreferrer"
										className="btn btn-sm btn-outline-secondary"
									>
										<i
											className="ri-download-2-line"
											aria-hidden
										/>{" "}
										Descargar
									</a>
								)}
								<button
									type="button"
									className="btn btn-sm btn-outline-primary"
									onClick={() => {
										setPopupOpen(false);
										inputRef.current?.click();
									}}
								>
									<i
										className="ri-upload-2-line"
										aria-hidden
									/>{" "}
									Reemplazar
								</button>
								<button
									type="button"
									className="btn btn-sm btn-outline-danger"
									onClick={() => {
										onFileChange(null);
										setPopupOpen(false);
									}}
								>
									<i
										className="ri-delete-bin-line"
										aria-hidden
									/>{" "}
									Quitar
								</button>
							</div>
							{sizeLabel && (
								<p className="resource-file-popup-meta">
									Tamano: {sizeLabel}
								</p>
							)}
						</div>
					</div>
				)}
				<input
					ref={inputRef}
					type="file"
					accept={accept}
					className="d-none"
					onChange={handleInputChange}
				/>
			</div>
		);
	}

	return (
		<div
			className={`resource-dropzone ${dragging ? "is-dragging" : ""}`}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
			onClick={() => inputRef.current?.click()}
			role="button"
			tabIndex={0}
			onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
		>
			<i
				className="ri-upload-cloud-2-line resource-dropzone-icon"
				aria-hidden
			/>
			<p className="resource-dropzone-text">
				Arrastra tu archivo aquí o{" "}
				<strong>haz clic para seleccionar</strong>
			</p>
			<input
				ref={inputRef}
				type="file"
				accept={accept}
				className="d-none"
				onChange={handleInputChange}
			/>
		</div>
	);
}

function ResourceForm({
	title,
	category,
	initial,
	pickerItemId,
	onSubmit,
	onClose,
	saving,
}) {
	const [titleVal, setTitleVal] = useState(initial?.title ?? "");
	const [urlVal, setUrlVal] = useState(initial?.url ?? "");
	const [textVal, setTextVal] = useState(initial?.text_content ?? "");
	const [file, setFile] = useState(null);
	const [titleError, setTitleError] = useState("");
	const contentType =
		pickerItemId === "file"
			? "file"
			: pickerItemId === "image"
				? "image"
				: pickerItemId === "video"
					? "video"
					: (initial?.content_type ?? "text");

	// Field visibility: each resource type exposes only what makes sense.
	const showFileUpload = contentType === "file" || contentType === "image";
	const showUrl =
		pickerItemId === "url" ||
		pickerItemId === "video" ||
		contentType === "video" ||
		(contentType === "text" && !pickerItemId);
	const showText =
		pickerItemId === "text" ||
		pickerItemId === "page" ||
		(contentType === "text" && pickerItemId !== "url");

	const urlLabel =
		pickerItemId === "video" || contentType === "video"
			? "URL del video (YouTube, Vimeo, etc.)"
			: "URL / Enlace";
	const urlPlaceholder =
		pickerItemId === "video" || contentType === "video"
			? "https://www.youtube.com/watch?v=..."
			: "https://...";

	const fileAccept =
		contentType === "image"
			? ".jpg,.jpeg,.png,.gif,.webp"
			: ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.odt,.ods,.odp,.csv,.rtf,.zip,.rar";

	const handleSubmit = (e) => {
		e.preventDefault();
		if (saving) return;
		if (!titleVal.trim()) {
			setTitleError("El titulo del recurso es obligatorio.");
			return;
		}
		setTitleError("");
		const payload = {
			title: titleVal.trim(),
			category: category || initial?.category || "material",
			content_type: contentType,
			url: urlVal.trim(),
			text_content: textVal.trim(),
		};
		onSubmit(payload, file || undefined);
	};

	const handleBackdrop = (e) => {
		if (e.target === e.currentTarget) onClose();
	};

	return (
		<div
			className="modal show d-block"
			style={{ background: "rgba(0,0,0,0.5)" }}
			tabIndex={-1}
			onClick={handleBackdrop}
			role="dialog"
			aria-modal="true"
			aria-labelledby="resource-modal-title"
		>
			<div
				className="modal-dialog modal-dialog-centered"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="modal-content">
					<div className="modal-header">
						<h5 id="resource-modal-title" className="modal-title">
							{capitalizeFirstLetter(title)}
						</h5>
						<button
							type="button"
							className="btn-close"
							onClick={onClose}
							aria-label="Cerrar"
						/>
					</div>
					<form onSubmit={handleSubmit}>
						<div className="modal-body">
							<div className="border rounded p-3 mb-3 bg-light-subtle">
								<div className="mb-2">
									<h6 className="mb-0">General</h6>
								</div>
								<div className="mb-0">
									<label
										className="form-label"
										htmlFor="resource-title"
									>
										<span>Nombre del recurso</span>
									</label>
									<input
										id="resource-title"
										type="text"
										className={`form-control ${titleError ? "is-invalid" : ""}`}
										value={titleVal} onChange={(e) => {
											setTitleVal(e.target.value);
											if (titleError) setTitleError("");
										}}
										placeholder="Ej: Guia semana 3"
										autoComplete="off"
										aria-invalid={!!titleError}
									/>
									{titleError && (
										<div className="invalid-feedback">
											<span>
												{capitalizeFirstLetter(
													titleError,
												)}
											</span>
										</div>
									)}
								</div>
							</div>

							{showFileUpload && (
								<div className="border rounded p-3 mb-3">
									<h6 className="mb-3">
										{contentType === "image"
											? "Imagen"
											: "Archivo"}
									</h6>
									<FileDropZone
										file={file}
										existingName={
											initial?.file_name || null
										}
										existingUrl={initial?.file || null}
										accept={fileAccept}
										onFileChange={setFile}
									/>
								</div>
							)}

							{showUrl && (
								<div className="border rounded p-3 mb-3">
									<h6 className="mb-3">{urlLabel}</h6>
									<input
										id="resource-url"
										type="url"
										className="form-control"
										value={urlVal}
										onChange={(e) => setUrlVal(e.target.value)}
										placeholder={urlPlaceholder}
									/>
								</div>
							)}

							{showText && (
								<div className="border rounded p-3">
									<h6 className="mb-3">
										Descripción (opcional)
									</h6>
									<RichTextEditor
										value={textVal}
										onChange={setTextVal}
										placeholder="Escribe una descripción o instrucciones..."
									/>
								</div>
							)}
						</div>
						<div className="modal-footer">
							<button
								type="button"
								className="adm-del-btn"
								onClick={onClose}
								disabled={saving}
							>
								<span>Cancelar</span>
							</button>
							<button
								type="submit"
								className="adm-save-btn"
								disabled={saving}
							>
								<span>
									{saving ? "Guardando..." : "Guardar"}
								</span>
							</button>
						</div>
					</form>
				</div>

			</div>
		</div>
	);
}

function ModalConfirm({ title, message, onConfirm, onClose, saving }) {
	const handleBackdrop = (e) => {
		if (e.target === e.currentTarget) onClose();
	};

	return (
		<div
			className="modal show d-block"
			style={{ background: "rgba(0,0,0,0.5)" }}
			tabIndex={-1}
			onClick={handleBackdrop}
			role="dialog"
			aria-modal="true"
			aria-labelledby="resource-modal-title"
		>
			<div
				className="modal-dialog modal-dialog-centered"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="modal-content">
					<div className="modal-header">
						<h5 className="modal-title">
							{capitalizeFirstLetter(title)}
						</h5>
						<button
							type="button"
							className="btn-close"
							onClick={onClose}
						/>
					</div>
					<div className="modal-body">
						<p className="mb-0">{message}</p>
					</div>
					<div className="modal-footer">
						<button
							type="button"
							className="adm-del-btn"
							onClick={onClose}
							disabled={saving}
						>
							<span>Cancelar</span>
						</button>
						<button
							type="button"
							className="adm-save-btn"
							onClick={onConfirm}
							disabled={saving}
						>
							<span>
								{saving
									? "Eliminando..."
									: "Confirmar eliminacion"}
							</span>
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
